import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link, useOutletContext } from 'react-router-dom';
import { Plus, Calendar, MapPin, Users, ChevronRight, Clock, Zap, Repeat, List, ChevronLeft, Upload, ArrowUp, ArrowDown, Filter, Layers, Search, Trash2, X } from 'lucide-react';
import api from '../services/api.js';
import { IS_NATIVE } from '../utils/apiBase.js';
import { useTeamRole } from '../hooks/useTeamRole.js';
import { useKeyboardSafeSheet } from '../hooks/useKeyboardSafeSheet.js';
import { useTimeFormat } from '../hooks/useTimeFormat.js';
import { formatDuration } from '../utils/formatDuration.js';
import { computeEndTime } from '../utils/computeEndTime.js';
import { dedupBySession } from '../utils/sessionDedup.js';
import toast from 'react-hot-toast';
import { PageSpinner } from '../components/Skeleton.jsx';
// 2026-09-28 — the team's league games (home + away), which aren't part
// of the team's own game list.
import LeagueGamesCard from '../components/LeagueGamesCard.jsx';
// v2.5.126 — Phase 11.2. Address inputs use Maps autocomplete.
import AddressAutocomplete from '../components/AddressAutocomplete.jsx';
import { format, addWeeks, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';
// 2026-06-10 — statutory-holiday markers for the month grid (same
// static dataset the dashboard MiniCalendar uses).
import { getHolidayMap } from '../utils/holidays.js';
// 2026-09-16 (per Gilles) — the Upcoming Reminders widget was removed from
// this page; the Dashboard already shows the same queue. (The import of
// UpcomingRemindersCard from DashboardPage went with it.)
import { getDevice } from '../hooks/useDevice.js';

// 2026-09-16 (per Gilles) — status chip wording + tooltips.
//   upcoming    → "Open"          check-in is open; the pre-game reminder
//                                 hasn't gone out yet.
//   roster_open → "Players Open"  the reminder went out (scheduler flips the
//                                 row to roster_open) and players are
//                                 checking in.
//   locked      → "Locked"        coach locked the roster by hand, OR (see
//                                 describeGameStatus) the game is beyond the
//                                 team's check-in look-ahead limit.
//   completed   → "Completed"     also what every past-dated game shows now,
//                                 whatever its stored status ("Past" is gone).
//   cancelled   → "Cancelled"
const STATUS_STYLES = {
  upcoming:    { bg: 'rgba(255,255,255,0.06)', text: '#7a9ab8',        border: 'rgba(122,154,184,0.25)', label: 'Open',
                 tip: 'Check-in is open — players can RSVP. The pre-game reminder email hasn\'t gone out yet.' },
  roster_open: { bg: 'var(--ice-a10)',    text: 'var(--ice)',          border: 'rgba(0,212,255,0.25)',   label: 'Players Open',
                 tip: 'The pre-game reminder has gone out and players are checking in.' },
  locked:      { bg: 'rgba(255,149,0,0.1)',    text: 'var(--puck)',    border: 'rgba(255,149,0,0.25)',   label: 'Locked',
                 tip: 'The coach has locked this roster — no more check-ins or check-outs from the app.' },
  completed:   { bg: 'rgba(0,255,136,0.1)',    text: 'var(--goal)',    border: 'rgba(0,255,136,0.25)',   label: 'Completed',
                 tip: 'This game has been played.' },
  cancelled:   { bg: 'rgba(255,68,68,0.1)',    text: 'var(--penalty)', border: 'rgba(255,68,68,0.25)',   label: 'Cancelled',
                 tip: 'This game was cancelled.' },
};

/**
 * 2026-09-16 — resolve what the status chip should SAY for a game, given
 * the stored status, the date, and the team's check-in look-ahead rule.
 * Pure function so the list card, the featured card and the calendar
 * agree.
 *
 * @param {object} game            row from GET /games
 * @param {boolean} isPastDate     game_date < today
 * @param {number|null} lookAhead  null = not limited; otherwise how many
 *                                 open games ahead of this one (0 = it's
 *                                 the next one)
 * @param {number|null} limit      team's checkin_lookahead_games
 */
function describeGameStatus(game, isPastDate, lookAhead, limit) {
  if (game.status === 'cancelled') return { ...STATUS_STYLES.cancelled, key: 'cancelled' };
  // Historical games are simply "Completed" — the stored status never
  // auto-flips when the date passes, and "Past" vs "Completed" was two
  // words for the same thing.
  if (isPastDate) return { ...STATUS_STYLES.completed, key: 'completed' };
  if (game.status === 'completed') return { ...STATUS_STYLES.completed, key: 'completed' };
  if (game.status === 'locked') return { ...STATUS_STYLES.locked, key: 'locked' };
  if (limit != null && lookAhead != null && lookAhead >= limit) {
    return {
      ...STATUS_STYLES.locked, key: 'locked_lookahead', auto: true,
      tip: `Check-in isn\'t open yet — this team only lets players RSVP for the next ${limit} game${limit === 1 ? '' : 's'}. It unlocks automatically once the games ahead of it are played.`,
    };
  }
  if (game.status === 'roster_open') return { ...STATUS_STYLES.roster_open, key: 'roster_open' };
  return { ...STATUS_STYLES.upcoming, key: 'upcoming' };
}

// Calendar-specific styles — use explicit rgba so shading is visible in both light and dark themes.
// The STATUS_STYLES.bg values are tuned for dark mode; these work in both.
const CAL_STYLES = {
  upcoming:    { bg: 'rgba(91,155,213,0.18)',  accent: '#5b9bd5',  label: 'Open' },
  roster_open: { bg: 'rgba(0,188,235,0.18)',   accent: '#00bce0',  label: 'Players Open' },
  locked:      { bg: 'rgba(255,149,0,0.18)',   accent: '#ff9500',  label: 'Locked' },
  completed:   { bg: 'rgba(0,200,100,0.18)',   accent: '#00c864',  label: 'Completed' },
  cancelled:   { bg: 'rgba(255,68,68,0.18)',   accent: '#ff4444',  label: 'Cancelled' },
};

const DAY_OF_WEEK = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Return the next occurrence of a given day-of-week (0=Sun) from a start date
function nextDayOfWeek(startDate, dayOfWeek) {
  const d = new Date(startDate);
  const diff = (dayOfWeek - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + diff);
  return d;
}

export default function SchedulePage() {
  const { teamId }               = useParams();
  const navigate                 = useNavigate();
  const location                 = useLocation();
  const { isCoach, loading: rL } = useTeamRole(teamId);

  // May 1 2026 — mobile/desktop gating for the coach action row.
  // Mobile (phone or capacitor): only "New Game" button. Desktop &
  // tablet: "New Game" + "Import". Auto-Schedule and Seasons live in
  // the More menu / sidebar — coaches reported the row was crowded
  // and overflowing horizontally on phones.
  const _device = getDevice();
  const _isMobile = _device.isPhone || _device.isCapacitor;

  // Read ?seasonId from URL (set by Seasons page "View Games" links)
  const urlSeasonId = new URLSearchParams(location.search).get('seasonId') || '';
  // v2.5.187 — URL ?seasonId wins (explicit link); else fall back to the
  // shared current season so Schedule matches the rest of the left-nav.
  const { currentSeasonId = null } = useOutletContext() || {};

  const [seasons,       setSeasons]       = useState([]);
  const [seasonFilter,  setSeasonFilter]  = useState(urlSeasonId || currentSeasonId || '');
  const { formatTime }           = useTimeFormat();

  const [games, setGames]         = useState([]);
  const [drinksMap, setDrinksMap] = useState({});
  // Apr 30 2026 — team-level default game duration. Used as the
  // fallback when a game row has no per-game `duration_minutes`
  // override, so the schedule list can still display "· 1.5 hr"
  // beside every start time. Loaded once per page lifetime.
  const [teamDefaultDuration, setTeamDefaultDuration] = useState(null);
  // 2026-06-10 — team country for month-grid holiday markers (see
  // loadGames). null = no country set = no markers (legacy behavior).
  const [teamCountry, setTeamCountry] = useState(null);
  // Memoized — getHolidayMap rebuilds three years of dates per call,
  // so don't invoke it per day-cell.
  const holidayMap = useMemo(() => getHolidayMap(teamCountry), [teamCountry]);
  // v2.5.100 — super-admin-controlled icon size for the availability
  // chips (✅ ❌ ⏳ 🤔). Read from the team response which merges
  // in system_settings.schedule_availability_icon_size. Default 16
  // matches the v2.5.97 baseline so coaches without the toggle set
  // see the same size as before.
  const [availIconSize, setAvailIconSize] = useState(16);
  const [loading, setLoading]     = useState(true);
  const [showNewGame, setShowNewGame]       = useState(false);
  const [showAutoSchedule, setShowAutoSchedule] = useState(false);
  const [sending, setSending]     = useState({});
  const [tab, setTab]             = useState('upcoming');
  // v2.5.186 — remember the user's preferred schedule view per device.
  // localStorage is inherently per-browser/device (desktop vs phone keep
  // their own choice), which is exactly what the coach asked for. Guarded
  // for SSR/older browsers; falls back to 'list'.
  const SCHED_VIEW_KEY = 'gameday:scheduleView';
  const [viewMode, setViewMode]   = useState(() => {
    try {
      const saved = localStorage.getItem(SCHED_VIEW_KEY);
      return saved === 'calendar' || saved === 'list' ? saved : 'list';
    } catch { return 'list'; }
  });
  const [calMonth, setCalMonth]   = useState(new Date());

  const [sortBy,      setSortBy]      = useState('date-asc');
  const [arenaFilter, setArenaFilter] = useState('');
  // v2.5.186 — persist the schedule view choice per device.
  useEffect(() => {
    try { localStorage.setItem(SCHED_VIEW_KEY, viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  // v2.5.185 — "iCal" quick button. Generates the user's calendar feed
  // token for this team if they don't have one yet, then copies the
  // https ICS URL to the clipboard with a toast (no trip to Profile).
  const [icalBusy, setIcalBusy] = useState(false);
  const [editGame, setEditGame]   = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [saving,   setSaving]     = useState(false);

  // Apr 30 2026 (App_Change_List #17) — Bulk season assignment state
  // removed. Season management is handled in the Seasons section per
  // the change list spec. The orphan state declarations
  // (selectedGames, showAssignSeason, assigningSeasonId, assigning)
  // and their handlers (toggleGameSelect, selectAllDisplayed,
  // assignGamesToSeason) were removed in this commit.

  // 2026-09-16 — Upcoming Reminders widget removed (duplicate of the
  // Dashboard's). Its `scheduledReminders` state + fetch went too, so
  // this page no longer hits GET /scheduled-reminders at all.

  // 2026-09-16 (per Gilles) — team settings needed by the status chips:
  // `checkin_lookahead_enabled` / `checkin_lookahead_games` decide whether
  // a future game is auto-"Locked" (players can't RSVP yet). Populated
  // from the same GET /teams/:id call loadGames already makes.
  const [teamSettings, setTeamSettings] = useState({});
  // 2026-09-17 — am I a goalie on this team? (goalie exemption from the
  // look-ahead lock, see Team Settings). From GET /teams/:id myPosition.
  const [myIsGoalie, setMyIsGoalie] = useState(false);

  const openEdit = (game) => {
    setEditGame(game);
    setEditForm({
      gameDate:     game.game_date,
      gameTime:     game.game_time?.slice(0, 5) || '20:00',
      arenaName:    game.arena_name || '',
      arenaAddress: game.arena_address || '',
      notes:        game.notes || '',
      opponent:     game.opponent_name || '',
      status:       game.status,
      // Apr 2026 (Tom_Notes B4) — per-game duration override.
      // Empty string = inherit team default.
      durationMinutes: game.duration_minutes ?? '',
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/teams/${teamId}/games/${editGame.id}`, {
        gameDate:     editForm.gameDate,
        gameTime:     editForm.gameTime,
        notes:        editForm.notes,
        opponent:     editForm.opponent,
        status:       editForm.status,
        // Apr 2026 (Tom_Notes B4) — pass duration. Empty string maps
        // to "clear override" on the backend (NULLs the column).
        durationMinutes: editForm.durationMinutes,
      });
      // Arena update goes through arena-override if changed
      if (editForm.arenaName !== (editGame.arena_name || '')) {
        await api.post(`/teams/${teamId}/games/${editGame.id}/arena-override`, {
          newArena:   editForm.arenaName,
          newAddress: editForm.arenaAddress,
          reason:     'Updated via schedule editor',
        }).catch(() => {}); // arena-override sends emails, ok if fails
      }
      toast.success('Game updated!');
      setEditGame(null);
      loadGames();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update game'); }
    setSaving(false);
  };

  const ef = (k) => (e) => setEditForm(p => ({ ...p, [k]: e.target.value }));

  // v2.5.185 — "iCal" toolbar button. Find (or create) this user's
  // calendar feed token for the current team, build the https ICS feed
  // URL, and copy it to the clipboard — no redirect to Profile.
  //
  // 2026-07-11 (user-reported feed error) — HOST FIXED. This helper
  // claimed to "mirror ProfilePage.calendarFeedUrl" but mirrored the
  // PRE-2026-07-07 version: it pointed production at api.game-day.app,
  // a hostname that does NOT resolve on this deployment (only
  // app.game-day.app exists; nginx proxies /api on the same origin —
  // the exact bug ProfilePage was cured of on 2026-07-07). Every feed
  // URL copied from THIS page therefore failed in subscribers'
  // calendar apps. Now: same-origin host in the browser;
  // app.game-day.app for native builds (no meaningful window origin).
  const calendarFeedUrl = (token) => {
    const host = (IS_NATIVE || typeof window === 'undefined' || !window.location?.host)
      ? 'app.game-day.app'
      : window.location.host;
    return `https://${host}/api/calendar/${token}.ics`;
  };
  const copyIcalLink = async () => {
    if (icalBusy) return;
    setIcalBusy(true);
    try {
      // Look for an existing token for THIS team first.
      let token = null;
      try {
        const { data } = await api.get('/me/calendar-tokens');
        const list = Array.isArray(data?.tokens) ? data.tokens : (Array.isArray(data) ? data : []);
        token = list.find(t => t.teamId === teamId)?.token || null;
      } catch { /* fall through to create */ }
      // None yet → create one for this team.
      if (!token) {
        const { data } = await api.post('/me/calendar-tokens', { teamId });
        token = data?.token || null;
      }
      if (!token) { toast.error('Could not create your calendar link'); return; }

      const url = calendarFeedUrl(token);
      // 2026-07-10 (batch-B A1) — the old code toasted "copied" no
      // matter what. writeText REJECTS when called after the awaits
      // above (the click's transient activation expired — Safari
      // always; Chrome whenever the document isn't focused, e.g.
      // DevTools open), and the execCommand fallback's return value
      // was ignored (it needs activation too, so it silently returned
      // false). Now: verified copy, or a prompt the user can copy from
      // manually — never a false success toast.
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch { /* fall through to execCommand */ }
      if (!copied) {
        try {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          copied = document.execCommand('copy') === true;
          document.body.removeChild(input);
        } catch { /* fall through to prompt */ }
      }
      if (copied) {
        toast.success('Calendar link copied — paste it into your calendar app to subscribe.', { duration: 5000 });
      } else {
        // Guaranteed path: show the link so the user can copy by hand.
        window.prompt('Copy your calendar link (Ctrl/Cmd+C):', url);
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not copy calendar link');
    } finally {
      setIcalBusy(false);
    }
  };

  const [newGame, setNewGame] = useState({
    gameDate: '', gameTime: '20:00', arenaName: '', arenaAddress: '', notes: '', opponent: '',
    seasonId: urlSeasonId || '',
  });

  // Apr 30 2026 (App_Change_List #17) — inline season creation
  // removed from both the New Game and Auto-Schedule modals. Season
  // management is handled exclusively in the Seasons section per the
  // change list spec. The state declarations and createInlineSeason
  // helpers were removed in this commit (see git history if you need
  // to restore the inline-create UX).

  const [autoForm, setAutoForm] = useState({
    dayOfWeek: '1',         // Monday
    startDate: '',
    numberOfGames: '10',
    gameTime: '20:00',
    arenaName: '',
    arenaAddress: '',
    skipDates: '',           // comma-separated YYYY-MM-DD
    seasonId: urlSeasonId || '', // Apr 2026 — attach all generated games to a season
    rosterLockOffset: '',    // hours before game to auto-lock roster ('' = manual)
    autoBalance: false,      // auto-generate balanced teams when roster locks
    regenCutoff: '',         // hours before game after which re-balance is blocked
  });

  // Sync URL ?seasonId param into seasonFilter when navigating here from Seasons page
  // (React Router doesn't remount the component when only the query string changes)
  useEffect(() => {
    setSeasonFilter(urlSeasonId || currentSeasonId || ''); // v2.5.187 fall back to current season
  }, [urlSeasonId, currentSeasonId]);

  // Apr 2026 — auto-open the "Schedule Game" modal when ?new=1 is in
  // the URL. Lets the Dashboard's "Schedule a Game" CTA deep-link
  // into the open modal without going through SchedulePage's UI.
  // straight into the popup instead of forcing a coach to land on
  // the schedule page and click a second button. Strips the param
  // afterwards so a refresh doesn't re-pop the modal.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('new') === '1' && isCoach) {
      setShowNewGame(true);
      params.delete('new');
      const newSearch = params.toString();
      navigate(
        `${location.pathname}${newSearch ? `?${newSearch}` : ''}`,
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCoach]);

  useEffect(() => { loadGames(); }, [teamId, seasonFilter]);
  useEffect(() => {
    api.get(`/teams/${teamId}/seasons`).then(r => setSeasons(r.data)).catch(() => {});
  }, [teamId]);

  // May 4 2026 — auto-default newGame.seasonId + autoForm.seasonId
  // to the team's active season when seasons load. Coach feedback:
  // "when I click +New Game, the season should already be there
  // since we're using the current active season for this environment.
  // It should not ask me to attach to a season — it should already
  // know." Only fires the FIRST time (when value is empty); a coach
  // who deliberately picks a different season for a one-off game
  // doesn't get their selection clobbered on the next seasons re-fetch.
  // URL ?seasonId param still wins (set during state init at line 144)
  // — this only fills in the default when no URL hint was provided.
  useEffect(() => {
    const active = seasons.find(s => s.isActive === true);
    if (!active) return;
    setNewGame(p => p.seasonId ? p : { ...p, seasonId: active.id });
    setAutoForm(p => p.seasonId ? p : { ...p, seasonId: active.id });
  }, [seasons]);

  const loadGames = async () => {
    try {
      const url = seasonFilter
        ? `/teams/${teamId}/games?seasonId=${seasonFilter}`
        : `/teams/${teamId}/games`;
      // Apr 30 2026 — also fetch the team so we can read
      // settings.default_game_duration_minutes for the per-row
      // duration display. Failure-tolerant: drinks and team load
      // both fall back to empty/null on error so a transient
      // failure doesn't blank the schedule.
      const [gamesRes, drinksRes, teamRes] = await Promise.all([
        api.get(url),
        api.get(`/teams/${teamId}/drinks`).catch(() => ({ data: {} })),
        api.get(`/teams/${teamId}`).catch(() => ({ data: null })),
      ]);
      setGames(gamesRes.data || []);
      setDrinksMap(drinksRes.data || {});
      const dur = teamRes.data?.settings?.default_game_duration_minutes;
      setTeamDefaultDuration(dur ? Number(dur) : null);
      setTeamSettings(teamRes.data?.settings || {}); // 2026-09-16 — lookahead lock
      setMyIsGoalie(teamRes.data?.myPosition === 'goalie' ||
        (Array.isArray(teamRes.data?.myPositions) && teamRes.data.myPositions.includes('goalie')));
      // 2026-06-10 (fix-verification pass) — team country for the
      // month-grid holiday markers. The original "show holidays on
      // all month grids" request only landed on the DASHBOARD grid
      // (v2.5.186/190); this page's grid never got them. Same
      // source as DashboardPage: settings.country (ISO alpha-2).
      setTeamCountry(teamRes.data?.settings?.country || null);
      // v2.5.100 — pull the super-admin-controlled chip size from the
      // team response. Whitelist guard mirrors the backend's so a
      // bad value can't break the layout (defaults to 16).
      const sz = Number(teamRes.data?.scheduleAvailabilityIconSize);
      if ([12, 16, 18, 20, 24, 28, 30].includes(sz)) setAvailIconSize(sz);
    } catch (err) {
      console.error('Schedule load error:', err);
      toast.error('Failed to load schedule — try refreshing');
    }
    setLoading(false);
  };

  const createGame = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/teams/${teamId}/games`, newGame);
      toast.success('Game scheduled! 🏟');
      setShowNewGame(false);
      // Apr 2026 — preserve seasonId on reset so a coach scheduling
      // multiple games in one sitting doesn't have to re-pick the
      // season every time. Any other field they typed (date/arena)
      // is intentionally cleared since each game is unique.
      setNewGame({ gameDate: '', gameTime: '20:00', arenaName: '', arenaAddress: '', notes: '', opponent: '', seasonId: newGame.seasonId || '' });
      loadGames();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to create game'); }
  };

  // Apr 30 2026 (App_Change_List #17) — createInlineSeason helpers removed.

  const generateSchedule = async (e) => {
    e.preventDefault();
    const skipRaw = autoForm.skipDates.split(',').map(s => s.trim()).filter(Boolean);
    // Fix #37: validate each skip date before sending — bad formats cause confusing
    // per-game errors from the API with no indication of which date was wrong.
    const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
    const badDates = skipRaw.filter(d => !DATE_RE.test(d) || isNaN(Date.parse(d)));
    if (badDates.length > 0) {
      toast.error(`Invalid skip date(s): ${badDates.join(', ')} — use YYYY-MM-DD format`);
      return;
    }
    const skip = skipRaw;
    const dow  = parseInt(autoForm.dayOfWeek);
    const n    = parseInt(autoForm.numberOfGames);
    const start = autoForm.startDate ? new Date(autoForm.startDate) : new Date();

    // Build list of game dates
    let firstGame = nextDayOfWeek(start, dow);
    const dates = [];
    let current = firstGame;
    while (dates.length < n) {
      const dateStr = format(current, 'yyyy-MM-dd');
      if (!skip.includes(dateStr)) dates.push(dateStr);
      current = addWeeks(current, 1);
    }

    // Create games sequentially
    let created = 0;
    const lockOffset  = autoForm.rosterLockOffset !== '' ? parseInt(autoForm.rosterLockOffset) : null;
    const regenCutoff = autoForm.regenCutoff      !== '' ? parseInt(autoForm.regenCutoff)      : null;
    // Apr 2026 — pass the seasonId from the autoForm so all
    // auto-generated games attach to the chosen season in one
    // batch. Empty string falls through as "no season" (legacy
    // behaviour).
    const seasonId = autoForm.seasonId || null;
    for (const gameDate of dates) {
      try {
        await api.post(`/teams/${teamId}/games`, {
          gameDate,
          gameTime:                autoForm.gameTime,
          arenaName:               autoForm.arenaName,
          arenaAddress:            autoForm.arenaAddress,
          seasonId,
          rosterLockOffsetHours:   lockOffset,
          autoBalanceOnLock:       autoForm.autoBalance,
          balanceRegenCutoffHours: regenCutoff,
        });
        created++;
      } catch {}
    }

    toast.success(`🗓️ Created ${created} of ${n} games!`);
    setShowAutoSchedule(false);
    loadGames();
  };

  // Apr 2026 — opens the audience-picker modal for the Reminder or
  // Req. Spares card button. Fetches the game detail (availability +
  // all_members) so we can compute concrete user-id buckets for each
  // checkbox option, then sets the modal's defaults per `kind`:
  //   reminder → "Full-timers who haven't responded" pre-checked
  //   spare    → "Spares who haven't said yes/no" pre-checked
  //              (NOT spares already on the waitlist — those have
  //               already RSVPed, no need to ask them again)
  const openAudiencePicker = async (kind, game) => {
    const key = `${game.id}-${kind}`;
    setSending(p => ({ ...p, [key]: true }));
    try {
      const { data } = await api.get(`/teams/${teamId}/games/${game.id}`);
      // Build buckets — userId arrays for each toggleable group.
      const av = data?.availability || [];
      const allMembers = (data?.all_members || []).filter(m => m.userId);
      const respondedIds = new Set(
        av.filter(a => a.userId && ['in', 'out', 'waitlist'].includes(a.status))
          .map(a => a.userId)
      );
      const inIds       = new Set(av.filter(a => a.userId && a.status === 'in').map(a => a.userId));
      const outIds      = new Set(av.filter(a => a.userId && a.status === 'out').map(a => a.userId));
      const waitlistIds = new Set(av.filter(a => a.userId && a.status === 'waitlist').map(a => a.userId));
      const ftAll       = allMembers.filter(m => m.isPrimary).map(m => m.userId);
      const spareAll    = allMembers.filter(m => !m.isPrimary).map(m => m.userId);
      // v2.5.226 — "Invite Spares" (kind 'spare') targets spare SKATERS only;
      // goalie-spares are NOT bulk-included on a skater spare request (same
      // skaters-only intent as the game-page Invite Spares modal + the auto-
      // invite cron). The jab/reminder kinds keep ALL spares since they're
      // general "you haven't responded yet" nudges. A coach can still add a
      // specific goalie-spare via the individual search-and-pick list below.
      const isGoalie = (m) =>
        m.position === 'goalie' ||
        (Array.isArray(m.positions) && m.positions.includes('goalie'));
      const spareBucketIds = kind === 'spare'
        ? allMembers.filter(m => !m.isPrimary && !isGoalie(m)).map(m => m.userId)
        : spareAll;
      const buckets = {
        ft_unresponded: ftAll.filter(id => !respondedIds.has(id)),
        ft_in:          ftAll.filter(id => inIds.has(id)),
        ft_out:         ftAll.filter(id => outIds.has(id)),
        spare_unresponded: spareBucketIds.filter(id => !respondedIds.has(id)),
        spare_waitlist:    spareBucketIds.filter(id => waitlistIds.has(id)),
        spare_in:          spareBucketIds.filter(id => inIds.has(id)),
      };
      // Apr 2026 — per-userId status map for the individual-pick list.
      // Lets the coach see "Bob — ✅ in" / "Alice — ⏳" next to each name
      // when manually ticking someone. Default (no row in availability)
      // means the player hasn't been seen on this game's RSVP — render
      // as "no response yet."
      const statusByUser = {};
      for (const a of av) {
        if (a.userId) statusByUser[a.userId] = a.status;
      }
      // Game label for the modal title.
      const gameLabel = `${new Date(game.game_date).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })} — ${game.game_time || ''}`.trim();
      // Per-kind default selection.
      // Apr 2026 (Tom_Notes B3) — added 'jab' kind for the per-game
      // jab button. Default selection is BOTH the FT and spare
      // unresponded groups so the coach can fire one shot at every-
      // body who hasn't said yes/no. Sent through the existing
      // send-reminder endpoint (the message is identical from the
      // recipient's perspective — "we're still waiting on your
      // RSVP"). Sticking with one backend endpoint avoids a new
      // template and migration.
      const defaultSel = new Set(
        kind === 'reminder' ? ['ft_unresponded']
        : kind === 'jab'    ? ['ft_unresponded', 'spare_unresponded']
        : ['spare_unresponded']
      );
      setAudienceSelection(defaultSel);
      // Apr 2026 — reset the per-player picks + search query so a
      // fresh open of the modal doesn't carry over leftover state
      // from a prior cancellation.
      setIndividualSelection(new Set());
      setAudienceSearch('');
      setAudiencePicker({ kind, gameId: game.id, gameLabel, buckets, allMembers, statusByUser });
    } catch (err) {
      toast.error('Failed to load game audience');
    }
    setSending(p => ({ ...p, [key]: false }));
  };

  const closeAudiencePicker = () => {
    if (audienceSending) return;
    setAudiencePicker(null);
    setAudienceSelection(new Set());
    setIndividualSelection(new Set());
    setAudienceSearch('');
  };

  const sendAudience = async () => {
    if (!audiencePicker) return;
    const { kind, gameId, buckets } = audiencePicker;
    // Union of selected buckets PLUS individually-picked userIds.
    // Deduped via Set so a user in both a checked group AND
    // individually ticked only gets one email. Apr 2026 — the
    // individualSelection is the new "search-and-pick a single
    // player" path; it complements the bucket toggles rather than
    // replacing them, and the union dedupes naturally.
    const recipientSet = new Set();
    for (const id of audienceSelection) {
      for (const uid of (buckets[id] || [])) recipientSet.add(uid);
    }
    for (const uid of individualSelection) recipientSet.add(uid);
    const userIds = [...recipientSet];
    if (userIds.length === 0) {
      toast.error('Pick at least one group or player');
      return;
    }
    setAudienceSending(true);
    try {
      // Apr 2026 (Tom_Notes B3) — 'jab' kind hits send-reminder
      // (same endpoint as a regular reminder) but with both
      // unresponded buckets pre-selected. Per-recipient template is
      // identical from the user's POV.
      const ep = (kind === 'reminder' || kind === 'jab')
        ? 'send-reminder'
        : 'send-spare-request';
      const { data } = await api.post(`/teams/${teamId}/games/${gameId}/${ep}`, { userIds });
      toast.success(data.message || `Sent to ${userIds.length} ${userIds.length === 1 ? 'player' : 'players'}`);
      setAudiencePicker(null);
      setAudienceSelection(new Set());
      setIndividualSelection(new Set());
      setAudienceSearch('');
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to send ${kind}`);
    }
    setAudienceSending(false);
  };

  // Legacy entry-point kept for any other internal callers; the card
  // buttons now go through openAudiencePicker so coaches always see
  // a who-gets-this preview before sending.
  const quickAction = async (action, gameId) => {
    const game = games.find(g => g.id === gameId);
    if (game) {
      await openAudiencePicker(action === 'reminder' ? 'reminder' : 'spare', game);
    }
  };

  // STATUS_CYCLE defines the allowed transitions coaches can click through
  const STATUS_CYCLE = ['upcoming', 'roster_open', 'locked', 'completed', 'cancelled'];
  const cycleStatus = async (game, e) => {
    e.stopPropagation();
    if (!isCoach) return;
    const idx  = STATUS_CYCLE.indexOf(game.status);
    const next = STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
    // Optimistic update
    setGames(prev => prev.map(g => g.id === game.id ? { ...g, status: next } : g));
    try {
      await api.put(`/teams/${teamId}/games/${game.id}`, { status: next });
    } catch {
      toast.error('Failed to update status');
      setGames(prev => prev.map(g => g.id === game.id ? { ...g, status: game.status } : g));
    }
  };

  // Apr 30 2026 (App_Change_List #17) — bulk-assign handlers removed.

  const [deleteGameId, setDeleteGameId] = useState(null);

  // Apr 2026 — Audience picker for the Reminder / Req. Spares card buttons.
  // Replaces the old fire-and-forget behaviour where tapping a button
  // blasted EVERY primary member or EVERY spare with no preview.
  // `audiencePicker` is null when closed; otherwise it's
  //   { kind: 'reminder' | 'spare' | 'jab', gameId, gameLabel, buckets, allMembers, statusByUser }
  // where `buckets` is the per-bucket roster, `allMembers` is the
  // flat decrypted member list for the individual-pick search bar,
  // and `statusByUser` maps userId → 'in'|'out'|'waitlist'|undefined
  // for the per-row status badge in the individual list.
  // `audienceSelection` is the set of bucket IDs the coach has toggled.
  // `individualSelection` is a parallel Set of explicitly-picked
  // userIds — added Apr 2026 so the coach can also jab a single
  // player by name without checking a whole bucket.
  // `audienceSearch` is the live query string for the individual list.
  // Defaults applied per-kind in openAudiencePicker.
  const [audiencePicker, setAudiencePicker] = useState(null);
  const [audienceSelection, setAudienceSelection] = useState(new Set());
  const [individualSelection, setIndividualSelection] = useState(new Set());
  const [audienceSearch, setAudienceSearch] = useState('');
  const [audienceSending, setAudienceSending] = useState(false);

  const deleteGame = async (gameId) => {
    // Apr 2026 — replaced the click-twice inline-confirm pattern with
    // a proper modal dialog. The old UX (`Delete` → `⚠ Confirm Delete?`
    // on second click) was easy to fat-finger on mobile and offered
    // no game-context (date/arena) at the moment of confirmation.
    // Now: first click opens a modal showing the game date + arena
    // and an explicit type-of confirmation flow ("Delete game" /
    // "Cancel"). The actual DELETE call only fires from the modal's
    // confirm button.
    setDeleteGameId(null);
    try {
      await api.delete(`/teams/${teamId}/games/${gameId}`);
      toast.success('Game deleted');
      loadGames();
    } catch { toast.error('Failed to delete'); }
  };

  const now = new Date();
  // May 4 2026 (v2.5.68) — fix bug where games stayed in "Upcoming" tab
  // forever once their date passed, because the filter included a
  // `|| g.status === 'roster_open'` exception that kept any roster-open
  // game in upcoming regardless of date. The status `roster_open` never
  // auto-flips (no cron marks games complete after the date), so a game
  // played weeks ago would still appear in upcoming if it hadn't been
  // explicitly closed. Coach screenshot showed a May 3 game in upcoming
  // on May 4. Fix: pure date comparison using start-of-day. A game on
  // 2026-05-03 belongs in past from 2026-05-04 00:00 onward, regardless
  // of status. Today's games stay in upcoming until midnight.
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // v2.5.93 — coach reported the schedule showed two cards for the
  // same May 10 game (a parent + sibling from a mini-game session).
  // The dashboard fix in v2.5.87 deduped its upcoming/recent lists
  // but the schedule page was missed. Same story: spawn-sibling
  // bumps game_time by a few seconds for stable sort, formatTime
  // hides seconds, the cards look identical except for status (one
  // completed with a real score, the other upcoming with no
  // score). Dedup by session root and prefer the meaningful row
  // for each tab — past wants the completed sibling (with the
  // score), upcoming wants the not-completed sibling.
  const upcomingGames = dedupBySession(games.filter(g => {
    const gameDay = new Date(g.game_date.slice(0,10) + 'T00:00:00');
    return gameDay >= startOfToday;
  }), 'upcoming');
  const pastGames = dedupBySession(games.filter(g => {
    const gameDay = new Date(g.game_date.slice(0,10) + 'T00:00:00');
    return gameDay < startOfToday;
  }), 'completed');
  const displayed     = tab === 'upcoming' ? upcomingGames : pastGames;

  // 2026-09-16 — check-in look-ahead ("Locked" chips). Mirrors the backend
  // guard in games.js PUT /availability: a game is too far ahead when the
  // number of not-completed / not-cancelled games between NOW and that
  // game (exclusive) is >= checkin_lookahead_games. Computed over the raw
  // (un-deduped) rows just like the SQL COUNT(*). Map: game id → how many
  // open games are ahead of it. Only built when the team setting is ON.
  // 2026-09-17 — goalies are exempt when the team setting says so: no
  // "Locked" chips for them (the backend guard skips them too).
  const lookAheadLimit = (teamSettings?.checkin_lookahead_enabled === true
      && !(teamSettings?.checkin_lookahead_exempt_goalies === true && myIsGoalie))
    ? Math.max(1, parseInt(teamSettings.checkin_lookahead_games, 10) || 2)
    : null;
  const lookAheadIndex = useMemo(() => {
    const map = new Map();
    if (lookAheadLimit == null) return map;
    const nowKey = format(now, 'yyyy-MM-dd') + 'T' + format(now, 'HH:mm:ss');
    const keyOf = g => `${g.game_date.slice(0, 10)}T${(g.game_time || '00:00:00').slice(0, 8)}`;
    const open = games
      .filter(g => !['completed', 'cancelled'].includes(g.status) && keyOf(g) >= nowKey)
      .sort((a, b) => keyOf(a).localeCompare(keyOf(b)));
    open.forEach((g, i) => map.set(g.id, i));
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [games, lookAheadLimit]);

  // 2026-09-16 — every scored game of each mini-game session, keyed by
  // session root (parent_game_id || id), in play order. Lets a card show
  // all five mini scores instead of only its representative row's.
  const sessionScores = useMemo(() => {
    const map = new Map();
    const scored = games.filter(g => g.result_home_score != null && g.result_away_score != null);
    scored.sort((a, b) => `${a.game_date}T${a.game_time || ''}`.localeCompare(`${b.game_date}T${b.game_time || ''}`));
    for (const g of scored) {
      const root = g.parent_game_id || g.id;
      if (!map.has(root)) map.set(root, []);
      map.get(root).push({
        id: g.id,
        home: g.result_home_score, away: g.result_away_score,
        homeLabel: g.home_team_label || 'Home', awayLabel: g.away_team_label || 'Away',
      });
    }
    return map;
  }, [games]);

  // 2026-09-16 — the "featured" card: the first upcoming game when the list
  // is in its natural order (earliest first, no arena filter). Any other
  // sort/filter = no featured card (the first row isn't "next" then).
  const featuredId = (tab === 'upcoming' && sortBy === 'date-asc' && !arenaFilter && upcomingGames.length > 0)
    ? [...upcomingGames].sort((a, b) => `${a.game_date}T${a.game_time || ''}`.localeCompare(`${b.game_date}T${b.game_time || ''}`))
        .find(g => g.status !== 'cancelled')?.id ?? null
    : null;
  const isFeaturedNext = (g) => g.id === featuredId;

  // Unique arenas from the currently displayed tab (non-empty names only)
  const arenas = useMemo(() => {
    const names = [...new Set(displayed.map(g => g.arena_name).filter(Boolean))].sort();
    return names;
  }, [displayed]);

  // Apply arena filter then sort
  const sortedFiltered = useMemo(() => {
    let list = arenaFilter
      ? displayed.filter(g => g.arena_name === arenaFilter)
      : [...displayed];

    list.sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          return a.game_date.localeCompare(b.game_date) || (a.game_time || '').localeCompare(b.game_time || '');
        case 'date-desc':
          return b.game_date.localeCompare(a.game_date) || (b.game_time || '').localeCompare(a.game_time || '');
        case 'arena-asc':
          return (a.arena_name || '').localeCompare(b.arena_name || '') || a.game_date.localeCompare(b.game_date);
        case 'arena-desc':
          return (b.arena_name || '').localeCompare(a.arena_name || '') || a.game_date.localeCompare(b.game_date);
        default:
          return 0;
      }
    });
    return list;
  }, [displayed, arenaFilter, sortBy]);

  // Calendar grid — build 6-week grid for calMonth
  const calDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(calMonth));
    const end   = endOfWeek(endOfMonth(calMonth));
    const days  = [];
    let d = start;
    while (d <= end) { days.push(d); d = addDays(d, 1); }
    return days;
  }, [calMonth]);

  // v2.5.93 — calendar/day-cell views also need dedup so a session
  // doesn't show as two dots/cards on the same day. Same algorithm
  // as the list views, with default 'latest' preference (the
  // calendar mixes upcoming and completed days; latest is the
  // sensible default for "what was the most recent thing here").
  const dedupedGamesForCalendar = useMemo(
    () => dedupBySession(games),
    [games]
  );

  const gamesOnDay = (day) =>
    dedupedGamesForCalendar.filter(g => isSameDay(parseISO(g.game_date), day));

  if (loading || rL) return (
    <PageSpinner />
  );

  return (
    <div className="animate-fade-in">
      {/* Header — tightened May 1 2026 to reduce vertical real
          estate above the schedule list. Was mb-5; reduced to mb-3
          to tighten the gap between the page header and the games. */}
      <div className="mb-3">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1 className="section-title text-4xl">Schedule</h1>
            <p className="text-secondary text-sm mt-1">
              {upcomingGames.length} upcoming · {pastGames.length} past
              {seasonFilter && seasons.find(s => s.id === seasonFilter) && (
                <span style={{ color: 'var(--ice)', marginLeft: 6 }}>
                  · {seasons.find(s => s.id === seasonFilter).name}
                </span>
              )}
            </p>
            {/* v2.5.185 — the primary "subscribe" action is now the
                "iCal" button in the toolbar below (copies the feed link
                straight to the clipboard). This stays as a quieter
                pointer to Profile for managing/revoking feed links. */}
            <button
              type="button"
              onClick={() => navigate('/profile#calendar')}
              className="text-xs mt-1 inline-flex items-center gap-1.5"
              style={{
                color: 'var(--text-secondary)',
                background: 'transparent',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}>
              <Calendar size={12} />
              Manage calendar subscriptions
            </button>
          </div>
        </div>
        {isCoach && (
          <div style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', marginTop: 8 }}>
            <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
              <button onClick={() => setShowNewGame(true)}
                className="btn-ice flex-shrink-0 text-sm py-2 px-3 flex items-center gap-1.5">
                <Plus size={14} /> New Game
              </button>
              {/* May 1 2026 — Import shown on desktop/tablet only.
                  On mobile the screen is too narrow for a wide button
                  row and coaches reported overflow scrolling. The
                  Import flow is still available from desktop or via
                  the (separate) ImportSchedulePage URL. */}
              {!_isMobile && (
                <button onClick={() => navigate(`/team/${teamId}/games/import`)}
                  className="btn-ghost text-xs px-3 py-2 flex-shrink-0 whitespace-nowrap flex items-center gap-1.5"
                  title="Import ice times from a PDF or screenshot">
                  <Upload size={13} /> Import
                </button>
              )}
              {/* May 1 2026 — Auto-Schedule and Seasons removed from
                  this row entirely (both mobile and desktop) per coach
                  feedback that the row felt crowded. Auto-Schedule is
                  still reachable from the empty-state CTA when the
                  team has no games; Seasons lives in the sidebar. */}
            </div>
          </div>
        )}

        {/* 2026-09-16 — Upcoming Reminders widget removed here (the
            Dashboard shows the same queue). */}

        {/* Season filter
            May 1 2026 — chip row removed entirely. User feedback:
            "I'm already in the correct season I should be in, so I
            shouldn't be seeing all these other options for seasons.
            There should be nothing between the new game button and
            the upcoming and past game buttons."
            The page still scopes to the team's active season by
            default (handled backend-side via the active_season_id
            filter on GET /games when no ?seasonId is passed). For
            cross-season browsing of historical games, coaches can
            navigate to the Seasons page and view per-season game
            lists from there. The seasonFilter state and effect
            stay in place because the URL ?seasonId param (set when
            navigating here from Seasons → "View Games") still
            scopes the list. */}
      </div>

      <LeagueGamesCard teamId={teamId} />

      {/* Tab + view toggle row — May 1 2026
          REMOVED. The Upcoming/Past tabs and List/Calendar view
          toggle have been folded into the compact sort+filter
          toolbar below (which only renders in list view, with the
          view toggle moved adjacent so it stays reachable). User
          feedback: too many bars between the page title and the
          actual schedule list — pushed real content "below the
          fold" on mobile. Reclaiming this row tightens the gap
          between the page header and the games. */}

      {/* ── Compact toolbar (May 1 2026) ──
          Was three separate rows: (a) season chips (removed earlier),
          (b) Upcoming/Past + List/Calendar toggle, (c) sort dropdown
          + arena filter. Coaches reported this pushed the actual
          schedule too far down on mobile. Now consolidated into ONE
          horizontally-scrollable strip:
            [Show: Upcoming/Past] [Sort dropdown] [Arena filter] [List/Calendar toggle]
          The strip renders for both list and calendar views — the
          calendar view ignores the sort dropdown and arena filter
          but still benefits from the show-tab and view-toggle being
          available without scrolling back up. */}
      <div className="mb-4">
        <div style={{ overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none', paddingBottom: 2 }}>
          <div className="flex items-center gap-2" style={{ minWidth: 'max-content' }}>
            {/* Show: Upcoming / Past — folded in from the old tab row */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <select
                value={tab}
                onChange={e => { setTab(e.target.value); setArenaFilter(''); }}
                aria-label="Show upcoming or past games"
                style={{
                  background: 'var(--ice-a10)',
                  border: '1px solid var(--ice-a30)',
                  color: 'var(--ice)',
                  borderRadius: 10, padding: '6px 10px', fontSize: 12,
                  fontWeight: 600, cursor: 'pointer', outline: 'none',
                }}>
                <option value="upcoming">Upcoming ({upcomingGames.length})</option>
                <option value="past">Past ({pastGames.length})</option>
              </select>
            </div>

            {/* Sort dropdown — only meaningful in list view, hidden
                in calendar view since the calendar lays out by date
                regardless of sort preference. */}
            {viewMode === 'list' && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <ArrowUp size={13}
                  style={{
                    color: 'var(--ice)',
                    transform: sortBy.endsWith('-desc') ? 'rotate(180deg)' : 'none',
                    transition: 'transform 0.15s',
                  }} />
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  aria-label="Sort games"
                  style={{
                    background: 'var(--ice-a10)',
                    border: '1px solid var(--ice-a30)',
                    color: 'var(--ice)',
                    borderRadius: 10, padding: '6px 10px', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', outline: 'none',
                  }}>
                  <option value="date-asc">Date — earliest first</option>
                  <option value="date-desc">Date — latest first</option>
                  <option value="arena-asc">Arena — A to Z</option>
                  <option value="arena-desc">Arena — Z to A</option>
                </select>
              </div>
            )}

            {/* Arena filter — only show when there are multiple
                arenas AND we're in list view (calendar shows all
                arenas via colour-coded markers regardless). */}
            {viewMode === 'list' && arenas.length > 1 && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <Filter size={13} style={{ color: arenaFilter ? 'var(--ice)' : 'var(--text-secondary)' }} />
                <select
                  value={arenaFilter}
                  onChange={e => setArenaFilter(e.target.value)}
                  style={{
                    background: arenaFilter ? 'var(--ice-a10)' : 'var(--surface-2)',
                    border: `1px solid ${arenaFilter ? 'var(--ice-a30)' : 'var(--border)'}`,
                    color: arenaFilter ? 'var(--ice)' : 'var(--text-secondary)',
                    borderRadius: 10, padding: '6px 10px', fontSize: 12,
                    fontWeight: 600, cursor: 'pointer', outline: 'none',
                  }}>
                  <option value="">All Arenas</option>
                  {arenas.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {arenaFilter && (
                  <button onClick={() => setArenaFilter('')}
                    title="Clear arena filter"
                    style={{
                      width: 24, height: 24, borderRadius: 6,
                      border: '1px solid var(--border)',
                      background: 'var(--surface-2)',
                      color: 'var(--text-2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', flexShrink: 0,
                    }}>
                    ×
                  </button>
                )}
              </div>
            )}

            {/* v2.5.185 — "iCal" quick-subscribe. Sits right of the
                sort/filter controls. One tap copies this user's ICS
                feed link to the clipboard (creating the token on first
                use) with a toast, instead of bouncing to Profile. */}
            <button
              type="button"
              onClick={copyIcalLink}
              disabled={icalBusy}
              title="Subscribe in your calendar app"
              aria-label="Subscribe in your calendar app"
              className="flex items-center gap-1.5 flex-shrink-0 rounded-xl transition-all"
              style={{
                background: 'var(--ice-a10)',
                border: '1px solid var(--ice-a30)',
                color: 'var(--ice)',
                padding: '6px 10px', fontSize: 12, fontWeight: 600,
                cursor: icalBusy ? 'default' : 'pointer',
                opacity: icalBusy ? 0.6 : 1,
              }}>
              <Calendar size={13} />
              {icalBusy ? 'Copying…' : 'iCal'}
            </button>

            {/* Spacer pushes the view toggle to the right edge */}
            <div style={{ flex: 1, minWidth: 8 }} />

            {/* List/Calendar view toggle — moved here from its own row.
                2026-09-16 (per Gilles) — bigger and more visible: 20px
                icons, 44px touch targets, a label on wider screens, and
                an ice-coloured active state with a border. */}
            <div className="flex gap-1 p-1 rounded-xl flex-shrink-0"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--ice-a30)' }}
              role="group" aria-label="Schedule view">
              {[['list', <List size={20} />, 'List'], ['calendar', <Calendar size={20} />, 'Month']].map(([mode, icon, label]) => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className="rounded-lg transition-all flex items-center gap-1.5"
                  aria-pressed={viewMode === mode}
                  title={mode === 'list' ? 'List view' : 'Month calendar view'}
                  style={{
                    padding: '8px 10px', minHeight: 40, minWidth: 44, justifyContent: 'center',
                    background: viewMode === mode ? 'var(--ice-a15)' : 'transparent',
                    border: `1px solid ${viewMode === mode ? 'var(--ice-a30)' : 'transparent'}`,
                    color: viewMode === mode ? 'var(--ice)' : 'var(--text-secondary)',
                    fontSize: 12, fontWeight: 700,
                  }}>
                  {icon}<span className="hidden sm:inline">{label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── End consolidated toolbar ── */}

      {/* 2026-07-10 (batch-B B2) added the month grid BELOW the list in
          list mode. 2026-09-16 (per Gilles) — reverted: the grid renders
          only in calendar view again; the list/calendar toggle in the
          toolbar (now bigger) is how you flip between them. The flex
          wrapper stays so the JSX below doesn't move. */}
      <div style={{ display: 'flex', flexDirection: 'column' }}>

      {/* ── Calendar view (calendar mode only) ── */}
      {viewMode === 'calendar' && (
        <div className="card mb-5" style={{ padding: '20px 20px 16px' }}>
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-5">
            <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
              className="p-2 rounded-xl transition-all"
              style={{ border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
              <ChevronLeft size={16} style={{ color: 'var(--text-secondary)' }} />
            </button>
            <div className="text-center">
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, letterSpacing: '0.06em', color: 'var(--text)' }}>
                {format(calMonth, 'MMMM yyyy').toUpperCase()}
              </h2>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                {(() => {
                  // v2.5.93 — count from deduped list so the badge
                  // doesn't claim 8 games when 4 of them are
                  // sibling-pairs of the same session.
                  const n = dedupedGamesForCalendar.filter(g => isSameMonth(parseISO(g.game_date), calMonth)).length;
                  return `${n} game${n !== 1 ? 's' : ''} this month`;
                })()}
              </p>
            </div>
            <button onClick={() => setCalMonth(d => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
              className="p-2 rounded-xl transition-all"
              style={{ border: '1px solid var(--border)', background: 'var(--surface-2)' }}>
              <ChevronRight size={16} style={{ color: 'var(--text-secondary)' }} />
            </button>
          </div>

          {/* Day-of-week headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 6 }}>
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
              <div key={d} style={{
                textAlign: 'center', fontSize: 11, fontWeight: 700,
                color: 'var(--text-secondary)', letterSpacing: '0.08em',
                padding: '4px 0', textTransform: 'uppercase',
                fontFamily: 'var(--font-display)',
              }}>{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3 }}>
            {calDays.map((day, i) => {
              const dayGames  = gamesOnDay(day);
              const inMonth   = isSameMonth(day, calMonth);
              const todayDay  = isToday(day);
              const hasGame   = inMonth && dayGames.length > 0;
              const firstGame = dayGames[0];
              // 2026-06-10 — holiday marker (parity with the dashboard
              // MiniCalendar; the original request was "all month grid
              // views show holidays" but this grid was missed).
              const holidayName = inMonth
                ? holidayMap.get(format(day, 'yyyy-MM-dd'))
                : null;
              // v2.5.186 — colour-coding fix. The cell colour keyed only
              // on firstGame.status, but status is unreliable for "done"
              // games: a game played via the mini-game flow (or simply
              // never flipped to 'completed') stays 'upcoming'/'locked',
              // so every cell rendered the same blue and the coach
              // couldn't tell played-vs-upcoming at a glance. We now
              // derive an effective status: a past-dated game (before
              // today) OR one with a recorded score is treated as
              // 'completed' for colouring; cancelled always wins.
              const effStatus = (() => {
                if (!firstGame) return null;
                if (firstGame.status === 'cancelled') return 'cancelled';
                const past = isBefore(startOfDay(parseISO(firstGame.game_date)), startOfDay(new Date()));
                const hasScore = firstGame.result_home_score != null && firstGame.result_away_score != null;
                if (firstGame.status === 'completed' || hasScore || past) return 'completed';
                return firstGame.status || 'upcoming';
              })();
              const cs = hasGame ? (CAL_STYLES[effStatus] || CAL_STYLES.upcoming) : null;
              // Apr 30 2026 (App_Change_List #7) — strike-through past
              // dates so users can tell at a glance which games have
              // already happened. Strict day-precision compare (NOT
              // including today). Applied as text-decoration on the
              // day number AND as opacity dimming on game-detail
              // content below so the cell still reads coherently.
              const isPast = inMonth && !todayDay && isBefore(startOfDay(day), startOfDay(new Date()));

              return (
                <div key={i}
                  onClick={() => hasGame && navigate(`/game/${firstGame.id}`)}
                  title={holidayName ? `📅 ${holidayName}` : undefined}
                  style={{
                    borderRadius: 10,
                    minHeight: 100,
                    padding: '8px 8px 7px',
                    cursor: hasGame ? 'pointer' : 'default',
                    opacity: inMonth ? 1 : 0.25,
                    transition: 'transform 0.12s, box-shadow 0.12s',
                    // Visible shading: game cells get solid tinted bg; empty in-month cells get subtle surface.
                    // 2026-06-10 — holiday tint on empty cells (game/today win).
                    background: hasGame
                      ? cs.bg
                      : todayDay
                      ? 'rgba(0,188,235,0.10)'
                      : holidayName
                      ? 'rgba(255,68,68,0.06)'
                      : inMonth ? 'var(--surface-2)' : 'transparent',
                    border: hasGame
                      ? `1px solid ${cs.accent}40`
                      : todayDay
                      ? '1px solid rgba(0,188,235,0.40)'
                      : holidayName
                      ? '1px solid rgba(255,68,68,0.18)'
                      : '1px solid var(--border)',
                    // Thick left accent bar is the primary status indicator
                    borderLeft: hasGame ? `4px solid ${cs.accent}` : undefined,
                    boxShadow: hasGame ? `0 1px 6px ${cs.accent}20` : 'none',
                  }}
                  onMouseEnter={e => {
                    if (hasGame) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = `0 4px 12px ${cs.accent}35`;
                    }
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'none';
                    e.currentTarget.style.boxShadow = hasGame ? `0 1px 6px ${cs.accent}20` : 'none';
                  }}
                >
                  {/* Day number (+ holiday corner dot, 2026-06-10) */}
                  <div style={{
                    fontSize: 13,
                    fontWeight: hasGame || todayDay ? 800 : 500,
                    color: hasGame ? cs.accent : todayDay ? '#00bce0' : holidayName ? '#dc6868' : 'var(--text-secondary)',
                    fontFamily: 'var(--font-display)',
                    lineHeight: 1,
                    marginBottom: hasGame ? 7 : 0,
                    display: 'flex', alignItems: 'center', gap: 5,
                    position: 'relative',
                    // App_Change_List #7 — strike past dates so it's
                    // unambiguous when scrolling.
                    textDecoration: isPast ? 'line-through' : 'none',
                    opacity: isPast ? 0.55 : 1,
                  }}>
                    {holidayName && (
                      <span style={{
                        position: 'absolute', top: 0, right: 0,
                        width: 6, height: 6, borderRadius: '50%',
                        background: '#dc6868',
                        boxShadow: '0 0 0 1.5px rgba(220,104,104,0.25)',
                      }} />
                    )}
                    {todayDay ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: 11,
                        background: hasGame ? cs.accent : '#00bce0',
                        color: '#fff', fontSize: 12, fontWeight: 800,
                      }}>
                        {format(day, 'd')}
                      </span>
                    ) : format(day, 'd')}
                  </div>

                  {/* 2026-06-10 — holiday name on empty cells (game
                      cells keep the corner dot + tooltip only; their
                      space is for game details). */}
                  {!hasGame && holidayName && (
                    <div style={{
                      fontSize: 9, fontWeight: 700, color: '#dc6868',
                      lineHeight: 1.3, marginTop: 6, wordBreak: 'break-word',
                      opacity: 0.9,
                    }}>
                      📅 {holidayName}
                    </div>
                  )}

                  {/* Game details */}
                  {hasGame && (
                    <div>
                      {/* Status label */}
                      <div style={{
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.1em',
                        textTransform: 'uppercase', color: cs.accent,
                        fontFamily: 'var(--font-display)', marginBottom: 4,
                        opacity: 0.85,
                      }}>
                        {cs.label}
                      </div>
                      {/* Time */}
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        marginBottom: 3,
                      }}>
                        <Clock size={10} style={{ color: cs.accent, flexShrink: 0 }} />
                        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)' }}>
                          {formatTime(firstGame.game_time)}
                          {/* Apr 30 2026 (App_Change_List #18) — end
                              time appended in subdued style. Calendar
                              cells are tight, so we drop the duration
                              chip here and rely on the start–end
                              range to convey it visually (a 7–9 PM
                              chip already implies a 2-hour game). */}
                          {(() => {
                            const durMin = firstGame.duration_minutes || teamDefaultDuration;
                            const endTime = computeEndTime(firstGame.game_time, durMin);
                            return endTime ? (
                              <span style={{ fontWeight: 500, opacity: 0.75 }}>
                                {' – '}{formatTime(endTime)}
                              </span>
                            ) : null;
                          })()}
                        </span>
                      </div>
                      {/* Arena — wraps, no clamp */}
                      {firstGame.arena_name && (
                        <div style={{
                          display: 'flex', alignItems: 'flex-start', gap: 4,
                          marginBottom: dayGames.length > 1 ? 3 : 0,
                        }}>
                          <MapPin size={9} style={{ color: cs.accent, flexShrink: 0, marginTop: 2 }} />
                          <span style={{
                            fontSize: 10, color: 'var(--text-secondary)',
                            lineHeight: 1.35, wordBreak: 'break-word',
                          }}>
                            {firstGame.arena_name}
                          </span>
                        </div>
                      )}
                      {/* Drinks indicator */}
                      {drinksMap[firstGame.id] && drinksMap[firstGame.id].status !== 'ignored' && (
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 3,
                          fontSize: 9, marginTop: 3,
                          color: drinksMap[firstGame.id].name ? '#00e87a' : '#ff3b3b',
                        }}>
                          <span>🥤</span>
                          <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 64 }}>
                            {drinksMap[firstGame.id].name
                              ? drinksMap[firstGame.id].name.split(' ')[0]
                              : 'No drinks!'}
                          </span>
                        </div>
                      )}
                      {/* Multiple games badge */}
                      {dayGames.length > 1 && (
                        <div style={{
                          display: 'inline-flex', alignItems: 'center',
                          fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                          color: '#fff', background: cs.accent,
                          borderRadius: 4, padding: '1px 5px',
                          fontFamily: 'var(--font-display)',
                        }}>
                          +{dayGames.length - 1} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--border)', flexWrap: 'wrap' }}>
            {Object.entries(CAL_STYLES).map(([key, cs]) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                <div style={{ width: 12, height: 12, borderRadius: 3, background: cs.bg, border: `2px solid ${cs.accent}`, flexShrink: 0 }} />
                {cs.label}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Result count — shown when arena is filtered (May 1 2026:
          old standalone toolbar removed, this line preserved as a
          standalone hint when filtering kicks in). */}
      {viewMode === 'list' && arenaFilter && (
        <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
          {sortedFiltered.length} game{sortedFiltered.length !== 1 ? 's' : ''} at {arenaFilter}
        </p>
      )}

      {/* Games list */}
      {viewMode === 'list' && (sortedFiltered.length === 0 ? (
        <div className="glass p-16 text-center text-secondary">
          <Calendar size={48} className="mx-auto mb-4 opacity-20" />
          <p className="font-medium">{arenaFilter ? `No ${tab} games at ${arenaFilter}` : `No ${tab} games`}</p>
          {isCoach && tab === 'upcoming' && !arenaFilter && (
            <>
              <button onClick={() => setShowNewGame(true)} className="btn-ice mt-4 mx-auto">
                <Plus size={15} /> Schedule First Game
              </button>
              {/* May 1 2026 — Auto-Schedule + Import secondary CTAs
                  on the empty state. The main coach action row was
                  reduced to "New Game" (mobile) / "New Game + Import"
                  (desktop) to clear visual clutter, but coaches with
                  no games still benefit from the bulk-create paths.
                  Showing them only when the schedule is empty keeps
                  the populated view clean. */}
              <div className="flex flex-col sm:flex-row gap-2 justify-center items-center mt-3">
                <button onClick={() => setShowAutoSchedule(true)}
                  className="btn-ghost text-xs px-3 py-2 flex items-center gap-1.5">
                  <Repeat size={13} /> Auto-Schedule a season
                </button>
                {!_isMobile && (
                  <button onClick={() => navigate(`/team/${teamId}/games/import`)}
                    className="btn-ghost text-xs px-3 py-2 flex items-center gap-1.5">
                    <Upload size={13} /> Import from PDF
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {sortedFiltered.map(game => {
            // v2.5.94 — the row's `status` column doesn't auto-flip
            // when game_date passes. A coach who didn't manually
            // mark the game completed leaves it in 'roster_open'
            // or 'upcoming' forever. v2.5.68 fixed the upcoming/
            // past TAB filter to use date as the source of truth,
            // but the per-row status PILL still rendered the stale
            // 'Upcoming' / 'Players Open' label on past-date games
            // — coach screenshot showed an Apr 26 game tagged
            // UPCOMING and a May 3 game tagged PLAYERS OPEN, both
            // weeks in the past. UI-only override: when a game's
            // date is past and status is still in a future-state
            // ('upcoming' / 'roster_open' / 'locked'), show 'Past'
            // instead. Completed/cancelled keep their existing
            // pills since those are coach-set terminal states.
            const gameDay = new Date(game.game_date.slice(0,10) + 'T00:00:00');
            const isPastDate = gameDay < startOfToday;
            // 2026-09-16 — status chip resolved by describeGameStatus:
            // past → Completed (was "Past" for un-flipped rows), future →
            // Open / Players Open / Locked (coach or look-ahead) / Cancelled.
            const st = describeGameStatus(game, isPastDate, lookAheadIndex.get(game.id) ?? null, lookAheadLimit);
            // 2026-09-16 — featured "next game" treatment: the first card
            // in the upcoming list (earliest-first, no arena filter) gets a
            // bigger, dashboard-style layout so the next game stands out.
            const featured = isFeaturedNext(game);
            // Coaches cycle the status by tapping the chip — except on a
            // look-ahead auto-lock (nothing stored to cycle) and on past
            // games (they always read Completed; use ✏️ Edit → Status to
            // mark one cancelled after the fact).
            const canCycle = isCoach && !st.auto && !isPastDate;
            const inCnt   = parseInt(game.players_in)       || 0;
            const goaliesIn = parseInt(game.goalies_in)     || 0;
            const skatersIn = Math.max(0, inCnt - goaliesIn);
            // 2026-09-16 — invite / jab markers (see games.js list query).
            const spareInvites = parseInt(game.spare_invites_sent_count) || 0;
            const invitesSent  = spareInvites > 0 || !!game.spare_request_sent_at;
            const invitesWhen  = game.spare_invites_last_at || game.spare_request_sent_at;
            const jabs         = parseInt(game.jabs_sent_count) || 0;
            const fmtWhen = (iso) => { try { return format(new Date(iso), 'MMM d, h:mm a'); } catch { return ''; } };
            // 2026-09-16 — every scored mini-game of this session (children
            // share parent_game_id; the parent itself may carry mini #1).
            const miniScores = sessionScores.get(game.parent_game_id || game.id) || [];
            const outCnt  = parseInt(game.players_out)      || 0;
            const waitCnt = parseInt(game.players_waitlist) || 0;
            // No-response = invited roster minus everyone who has
            // responded. v2.5.147 — was `total_members` (every team
            // member). Switched to `invited_count` (FT + spares who
            // actually got invite emails for this game). Without
            // the switch a team with 50 passive spares showed "50
            // awaiting" on every game — most of those 50 had never
            // been asked to play. Pending counts as a response (the
            // player initiated the spare-pay flow), maybe is folded
            // in too since it still represents an explicit reply.
            //
            // Fallback to total_members for legacy responses before
            // the backend exposes invited_count (e.g. cached payload
            // mid-deploy). Once the new field is in the wire format
            // it dominates. v2.5.147 audit A2 — use nullish-aware
            // check so a true zero (nobody invited yet) doesn't
            // silently fall back to total_members and re-introduce
            // the bug.
            const invitedRoster = game.invited_count != null
              ? (parseInt(game.invited_count) || 0)
              : (parseInt(game.total_members) || 0);
            const respondedCnt  = inCnt + outCnt + waitCnt
                                + (parseInt(game.players_maybe)   || 0)
                                + (parseInt(game.players_pending) || 0);
            const noRespCnt     = Math.max(0, invitedRoster - respondedCnt);
            const date  = format(parseISO(game.game_date), 'EEE, MMM d');
            const year  = format(parseISO(game.game_date), 'yyyy');

            return (
              <div key={game.id} className="glass-hover rounded-2xl"
                style={{
                  overflow: 'hidden',
                  // 2026-09-16 — featured next game: ice-coloured frame + glow,
                  // mirrors the Dashboard's Next Game hero.
                  ...(featured ? {
                    border: '1.5px solid var(--ice-a30)',
                    boxShadow: '0 0 0 3px var(--ice-a10), 0 10px 30px rgba(0,0,0,0.18)',
                    background: 'linear-gradient(180deg, var(--ice-a8) 0%, transparent 60%)',
                  } : {}),
                }}>
                {featured && (
                  <div className="flex items-center gap-2"
                    style={{
                      padding: '7px 14px', background: 'var(--ice)', color: '#001018',
                      fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: 11,
                      letterSpacing: '0.18em', textTransform: 'uppercase',
                    }}>
                    <Zap size={12} /> Next game
                    <span style={{ marginLeft: 'auto', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'none', opacity: 0.85 }}>
                      {(() => {
                        const days = Math.round((gameDay - startOfToday) / 86400000);
                        return days === 0 ? 'Today' : days === 1 ? 'Tomorrow' : `In ${days} days`;
                      })()}
                    </span>
                  </div>
                )}
                {/* v2.5.154 — mobile-native card redesign. Goals:
                    • A clean left date rail with a subtle status accent
                      bar instead of the floating date block.
                    • One tidy header row (date + status), with the
                      "you're in/out" state shown as a compact dot+label
                      rather than a competing pill.
                    • Availability rendered as an even stat strip so the
                      numbers line up instead of wrapping mid-row.
                    • Coach actions as soft, evenly-weighted pills that
                      read like native iOS list actions.
                    All data/logic is unchanged from the prior version. */}
                <div className="flex items-stretch" onClick={() => navigate(`/game/${game.id}`)}
                  style={{ cursor: 'pointer', WebkitTapHighlightColor: 'transparent' }}>
                  {/* Status accent rail */}
                  <div style={{ width: 4, flexShrink: 0, background: st.text, opacity: 0.9 }} />

                  {/* Date block */}
                  <div className="flex-shrink-0 flex flex-col items-center justify-center"
                    style={{ width: featured ? 76 : 60, padding: '14px 0', gap: 1 }}>
                    <div className="section-title" style={{ fontSize: featured ? 40 : 26, lineHeight: 1 }}>
                      {format(parseISO(game.game_date), 'd')}
                    </div>
                    <div className="text-secondary uppercase" style={{ fontSize: 10, letterSpacing: '0.08em', fontWeight: 700 }}>
                      {format(parseISO(game.game_date), 'MMM')}
                    </div>
                    <div className="text-secondary uppercase" style={{ fontSize: 9, letterSpacing: '0.06em', opacity: 0.6 }}>
                      {format(parseISO(game.game_date), 'EEE')}
                    </div>
                  </div>

                  <div className="flex-1 min-w-0" style={{ padding: '13px 12px 13px 2px' }}>
                    {/* Header row — title + status */}
                    <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
                      <span className="font-semibold truncate" style={{ fontSize: featured ? 18 : 15, flex: '0 1 auto' }}>
                        {game.opponent_name
                          ? <>vs {game.opponent_name}<span className="text-secondary" style={{ fontWeight: 500, opacity: 0.7 }}> · {date}</span></>
                          : <>{date}<span className="text-secondary" style={{ fontWeight: 500, opacity: 0.7 }}>, {year}</span></>}
                      </span>
                      {/* Status chip — 2026-09-16: tooltip explains the state
                          (title on desktop; tap on mobile toasts it for
                          players, coaches still tap to cycle the status). An
                          auto-"Locked" (look-ahead) chip can't be cycled — the
                          stored status isn't 'locked', the rule is. */}
                      <span
                        className="text-xs rounded-full transition-all flex-shrink-0"
                        style={{
                          background: st.bg, color: st.text,
                          padding: '2px 9px', fontWeight: 700,
                          fontSize: 10.5, letterSpacing: '0.02em',
                          cursor: canCycle ? 'pointer' : 'help',
                          border: st.auto ? `1px dashed ${st.text}` : '1px solid transparent',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (canCycle) return cycleStatus(game, e);
                          toast(st.tip, { icon: st.auto ? '🔒' : 'ℹ️', duration: 4500 });
                        }}
                        title={canCycle ? `${st.tip}\n\nClick to change status (current: ${st.label})` : st.tip}>
                        {st.label.toUpperCase()}{canCycle ? ' ↻' : ''}
                      </span>
                      {/* 2026-09-16 — invite / jab markers beside the status.
                          Shown to coaches on upcoming games (a player doesn't
                          need to know the coach's outreach). */}
                      {isCoach && !isPastDate && invitesSent && (
                        <span className="flex-shrink-0 rounded-full"
                          style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', color: 'var(--puck)', background: 'var(--puck-a8)', border: '1px solid var(--puck-a15)', cursor: 'help' }}
                          onClick={(e) => { e.stopPropagation(); toast(`Spare invites sent${spareInvites ? ` to ${spareInvites} spare${spareInvites === 1 ? '' : 's'}` : ''}${invitesWhen ? ` — last on ${fmtWhen(invitesWhen)}` : ''}.`, { icon: '✉️' }); }}
                          title={`Spare invites have gone out for this game${spareInvites ? ` (${spareInvites} spare${spareInvites === 1 ? '' : 's'})` : ''}${invitesWhen ? ` — last sent ${fmtWhen(invitesWhen)}` : ''}.`}>
                          ✉️ Invited{spareInvites ? ` ${spareInvites}` : ''}
                        </span>
                      )}
                      {isCoach && !isPastDate && jabs > 0 && (
                        <span className="flex-shrink-0 rounded-full"
                          style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', color: 'var(--ice)', background: 'var(--ice-a8)', border: '1px solid var(--ice-a15)', cursor: 'help' }}
                          onClick={(e) => { e.stopPropagation(); toast(`Jab sent to ${jabs} player${jabs === 1 ? '' : 's'}${game.jabs_last_at ? ` — last on ${fmtWhen(game.jabs_last_at)}` : ''}.`, { icon: '⚡' }); }}
                          title={`Players have been jabbed for this game (${jabs} player${jabs === 1 ? '' : 's'})${game.jabs_last_at ? ` — last sent ${fmtWhen(game.jabs_last_at)}` : ''}.`}>
                          ⚡ Jabbed {jabs}
                        </span>
                      )}
                      {/* "you're in/out" — compact trailing state.
                          2026-09-16: also OUT-after-IN is just OUT (the row
                          holds the latest status), plus NO-SHOW (mig 109 flag,
                          coach-set) and WAITLIST / MAYBE so a player always
                          sees where they stood for every game. */}
                      {(() => {
                        const my = game.my_no_show
                          ? { label: 'NO-SHOW', color: 'var(--puck)', icon: '👻' }
                          : game.my_status === 'in'       ? { label: 'IN',       color: 'var(--goal)' }
                          : game.my_status === 'out'      ? { label: 'OUT',      color: 'var(--penalty)' }
                          : game.my_status === 'waitlist' ? { label: 'WAITLIST', color: '#a78bfa' }
                          : game.my_status === 'maybe'    ? { label: 'MAYBE',    color: 'var(--puck)' }
                          : null;
                        if (!my) return null;
                        return (
                          <span className="flex items-center gap-1 flex-shrink-0"
                            style={{ color: my.color, fontSize: featured ? 12.5 : 11, fontWeight: 700, marginLeft: 'auto' }}
                            title={game.my_no_show ? 'You were checked in but flagged as a no-show for this game' : `Your status for this game: ${my.label}`}>
                            {my.icon
                              ? <span style={{ fontSize: '1.1em', lineHeight: 1 }}>{my.icon}</span>
                              : <span style={{ width: 6, height: 6, borderRadius: 999, background: my.color }} />}
                            {my.label}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Time + arena — quieter secondary line */}
                    <div className="flex items-center gap-2 mt-1.5 text-secondary flex-wrap" style={{ fontSize: 12 }}>
                      <span className="flex items-center gap-1 flex-shrink-0">
                        <Clock size={11} />{formatTime(game.game_time)}
                        {(() => {
                          const durMin = game.duration_minutes || teamDefaultDuration;
                          const dur     = formatDuration(durMin);
                          const endTime = computeEndTime(game.game_time, durMin);
                          return (
                            <>
                              {endTime && <span style={{ opacity: 0.6 }}> – {formatTime(endTime)}</span>}
                              {dur && <span style={{ opacity: 0.6 }}> · {dur}</span>}
                            </>
                          );
                        })()}
                      </span>
                      {game.arena_name && (
                        <span className="flex items-center gap-1 min-w-0">
                          <MapPin size={11} style={{ flexShrink: 0 }} />
                          <span className="truncate" style={{ maxWidth: 130 }}>{game.arena_name}</span>
                        </span>
                      )}
                    </div>

                    {/* Availability stat strip — v2.5.154. Even,
                        aligned cells instead of wrapping emoji chips.
                        Each cell keeps the title tooltips + the super-
                        admin icon-size control (availIconSize) so the
                        accessibility A/B still applies. Goalie split
                        rides along inside the IN cell as before.
                        Wraps gracefully if availIconSize is cranked up
                        via the super-admin control. */}
                    {/* 2026-09-16 (per Gilles) — the first cell is PLAYERS ONLY
                        (skaters checked in) with a player icon; goalies keep
                        their own 🥅 cell. Previously ✅ showed players+goalies
                        combined, which read as "19 in" when it was 17 + 2.
                        Matches the Dashboard's "Players / Goalies" split. */}
                    <div className="flex items-center mt-2.5 flex-wrap" style={{ gap: featured ? 18 : 14 }}>
                      <span className="flex items-center gap-1 flex-shrink-0"
                        style={{ color: 'var(--goal)', opacity: skatersIn === 0 ? 0.4 : 1, fontSize: featured ? availIconSize + 6 : availIconSize, fontWeight: 700 }}
                        title={`${skatersIn} player${skatersIn !== 1 ? 's' : ''} checked in (goalies counted separately)`}>
                        🏒 {skatersIn}
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0"
                        style={{ color: 'var(--goal)', opacity: goaliesIn === 0 ? 0.4 : 1, fontSize: featured ? availIconSize + 6 : availIconSize, fontWeight: 700 }}
                        title={`${goaliesIn} goalie${goaliesIn !== 1 ? 's' : ''} checked in`}>
                        🥅 {goaliesIn}
                      </span>
                      <span className="flex items-center gap-1 flex-shrink-0"
                        style={{ color: 'var(--penalty)', opacity: outCnt === 0 ? 0.4 : 1, fontSize: featured ? availIconSize + 6 : availIconSize, fontWeight: 700 }}
                        title={`${outCnt} out`}>
                        ❌ {outCnt}
                      </span>
                      {waitCnt > 0 && (
                        <span className="flex items-center gap-1 flex-shrink-0"
                          style={{ color: '#a78bfa', fontSize: featured ? availIconSize + 6 : availIconSize, fontWeight: 700 }}
                          title={`${waitCnt} on waitlist`}>
                          ⏳ {waitCnt}
                        </span>
                      )}
                      <span className="flex items-center gap-1 flex-shrink-0"
                        style={{ color: 'var(--puck)', opacity: noRespCnt === 0 ? 0.4 : 1, fontSize: featured ? availIconSize + 6 : availIconSize, fontWeight: 700 }}
                        title={`${noRespCnt} awaiting response`}>
                        🤔 {noRespCnt}
                      </span>
                    </div>

                    {/* Drinks line — own row so it never crowds the
                        stat strip; only shown when assigned. */}
                    {drinksMap[game.id] && drinksMap[game.id].status !== 'ignored' && (
                      <div className="flex items-center gap-1 mt-2" style={{ fontSize: 12.5, fontWeight: 600,
                        color: drinksMap[game.id].name ? 'var(--goal)' : 'var(--penalty)' }}>
                        🥤 {drinksMap[game.id].name || 'No drinks volunteer yet'}
                      </div>
                    )}

                    {/* 2026-09-16 (per Gilles) — show EVERY scored mini-game of
                        the session, not just this row's own score. One chip per
                        mini in play order; a single-game night still reads
                        "Final: a — b". */}
                    {miniScores.length > 1 ? (
                      <div className="mt-2 flex items-center flex-wrap" style={{ gap: 6 }}
                        title={miniScores.map((m, i) => `Mini ${i + 1}: ${m.homeLabel} ${m.home} — ${m.away} ${m.awayLabel}`).join('\n')}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                          {miniScores.length} mini games
                        </span>
                        {miniScores.map((m, i) => (
                          <span key={m.id} className="rounded-md"
                            style={{ fontSize: 12, fontWeight: 800, color: 'var(--goal)', background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.22)', padding: '1px 7px', fontFamily: 'var(--font-display)' }}>
                            <span style={{ opacity: 0.6, fontWeight: 700, marginRight: 4 }}>G{i + 1}</span>{m.home}–{m.away}
                          </span>
                        ))}
                      </div>
                    ) : miniScores.length === 1 ? (
                      <div className="mt-1.5" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--goal)' }}>
                        Final: {miniScores[0].home} — {miniScores[0].away}
                      </div>
                    ) : game.result_home_score != null ? (
                      <div className="mt-1.5" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--goal)' }}>
                        Final: {game.result_home_score} — {game.result_away_score}
                      </div>
                    ) : null}
                  </div>

                  {/* Disclosure chevron — vertically centered at the
                      right edge, the standard native list affordance. */}
                  <div className="flex items-center flex-shrink-0" style={{ paddingRight: 12 }}>
                    <ChevronRight size={18} className="text-secondary" style={{ opacity: 0.5 }} />
                  </div>
                </div>

                {/* Coach quick actions — v2.5.154 native-style pills */}
                {isCoach && (
                  <div style={{ padding: '0 12px 12px 16px' }}
                    onClick={e => e.stopPropagation()}>
                    <div className="flex gap-2" style={{ flexWrap: 'wrap' }}>
                      {tab === 'upcoming' && (<>
                      <button
                        onClick={() => quickAction('spare', game.id)}
                        disabled={!!sending[`${game.id}-spare`]}
                        className="flex items-center justify-center gap-1.5 transition-all"
                        style={{ flex: '1 1 0', minWidth: 104, padding: '9px 12px', borderRadius: 10,
                          fontSize: 12.5, fontWeight: 600,
                          background: 'var(--puck-a8)', border: '1px solid var(--puck-a15)', color: 'var(--puck)' }}>
                        {sending[`${game.id}-spare`]
                          ? <div className="spinner" style={{ width: 12, height: 12 }} />
                          : <Users size={13} />}
                        Invite Spares
                      </button>
                      <button
                        onClick={() => openAudiencePicker('jab', game)}
                        disabled={!!sending[`${game.id}-jab`]}
                        className="flex items-center justify-center gap-1.5 transition-all"
                        style={{ flex: '1 1 0', minWidth: 104, padding: '9px 12px', borderRadius: 10,
                          fontSize: 12.5, fontWeight: 600,
                          background: 'var(--ice-a8)', border: '1px solid var(--ice-a15)', color: 'var(--ice)' }}>
                        {sending[`${game.id}-jab`]
                          ? <div className="spinner" style={{ width: 12, height: 12 }} />
                          : <Zap size={13} />}
                        Jab Players
                      </button>
                      </>)}
                      <button
                        onClick={() => openEdit(game)}
                        className="flex items-center justify-center gap-1.5 transition-all"
                        style={{ flex: tab === 'upcoming' ? '0 0 auto' : '1 1 0', minWidth: 64, padding: '9px 14px', borderRadius: 10,
                          fontSize: 12.5, fontWeight: 600,
                          background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        ✏️ Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* 2026-07-10 (batch-B B2) — close the flex-order wrapper. */}
      </div>

      {/* ── Single game modal ── */}
      {showNewGame && (
        <Modal title="Schedule Game" onClose={() => setShowNewGame(false)}>
          <form onSubmit={createGame} className="space-y-4">

            {/* May 4 2026 (v2.5.60) — season picker removed from this
                modal per coach feedback: the active season is already
                known from the page context, and surfacing the picker
                made coaches second-guess a choice they didn't need to
                make. The active season is auto-selected (logic in the
                useEffect at line ~199) and the POST payload still
                includes seasonId, so games are still attached to the
                right season — there's just no UI to override it from
                this modal. Coaches who need to attach a game to a
                different season can do so from the Seasons page after
                the game is created.

                Empty-state behavior preserved: when the team has no
                seasons yet, the small "no seasons → create one" prompt
                still renders, since it's informational rather than a
                picker. */}
            {(() => {
              const activeSeasons = seasons.filter(s => s.isActive !== false);
              if (activeSeasons.length === 0) {
                return (
                  <div className="p-3 rounded-xl" style={{
                    background: 'rgba(0,212,255,0.05)',
                    border: '1.5px solid var(--ice-a30)',
                  }}>
                    <div className="flex items-center gap-2 mb-2">
                      <Layers size={16} style={{ color: 'var(--ice)' }} />
                      <p className="text-sm font-semibold" style={{ color: 'var(--ice)' }}>No seasons yet</p>
                    </div>
                    <p className="text-xs text-secondary" style={{ lineHeight: 1.5 }}>
                      Game will be saved without a season. You can create
                      a season and attach this game later from the{' '}
                      <Link to={`/team/${teamId}/seasons`}
                        className="underline"
                        style={{ color: 'var(--ice)' }}>
                        Seasons page
                      </Link>.
                    </p>
                  </div>
                );
              }
              // When at least one season exists, the picker is hidden.
              // newGame.seasonId is auto-set to the active season by
              // the useEffect at line ~199; we just trust that here.
              return null;
            })()}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Date *</label>
                <input type="date" required className="input-field"
                  value={newGame.gameDate} onChange={e => setNewGame(p => ({ ...p, gameDate: e.target.value }))} />
              </div>
              <div>
                <label className="label">Time *</label>
                <input type="time" required className="input-field"
                  value={newGame.gameTime} onChange={e => setNewGame(p => ({ ...p, gameTime: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">Opponent</label>
              <input className="input-field" type="text" placeholder="Kitchener Rangers (leave blank for a pickup night)" maxLength={120}
                value={newGame.opponent} onChange={e => setNewGame(p => ({ ...p, opponent: e.target.value }))} />
            </div>
            <div>
              <label className="label">Arena Name</label>
              <input className="input-field" type="text" placeholder="Main St Arena"
                value={newGame.arenaName} onChange={e => setNewGame(p => ({ ...p, arenaName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Arena Address</label>
              <AddressAutocomplete className="input-field" placeholder="123 Arena Lane"
                value={newGame.arenaAddress} onChange={(v) => setNewGame(p => ({ ...p, arenaAddress: v }))} />
            </div>
            {/* Apr 2026 — Game Notes input removed per user request. */}
            {/* Apr 2026 — Inline Season picker hoisted to top of modal
                (see season-picker block above). The duplicated block
                that used to live here was removed in the rebuild. */}
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowNewGame(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-ice flex-1 justify-center">Schedule</button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Auto-schedule modal ── */}
      {showAutoSchedule && (
        <Modal title="🗓️ Auto-Schedule Season" onClose={() => setShowAutoSchedule(false)}>
          <p className="text-sm text-secondary mb-4">
            Auto-generate a full season's worth of games on a recurring day of the week.
          </p>
          <form onSubmit={generateSchedule} className="space-y-4">

            {/* Apr 2026 — season picker added to auto-schedule modal.
                Coaches who use auto-schedule almost always want all
                generated games on the same season (that's the whole
                point — "Fall 2026" is N games on the same recurring
                slot). The picker mirrors the Schedule Game modal's
                inline-create flow so a coach with no seasons can
                spin one up without losing the auto-schedule form
                they already filled in. */}
            <div className="p-3 rounded-xl" style={{
              background: 'rgba(0,212,255,0.05)',
              border: '1.5px solid var(--ice-a30)',
            }}>
              {/* Apr 30 2026 (App_Change_List #17) — inline season
                  creation removed from this modal too. Picker only;
                  link to Seasons page for management. */}
              {(() => {
                const activeSeasons = seasons.filter(s => s.isActive !== false);
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Layers size={16} style={{ color: 'var(--ice)' }} />
                      <p className="text-sm font-semibold" style={{ color: 'var(--ice)' }}>Season for these games</p>
                    </div>
                    {activeSeasons.length > 0 ? (
                      <>
                        <select className="input-field"
                          value={autoForm.seasonId}
                          onChange={e => setAutoForm(p => ({ ...p, seasonId: e.target.value }))}>
                          <option value="">— No season —</option>
                          {activeSeasons.map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                        <p className="text-xs text-secondary mt-1.5" style={{ lineHeight: 1.4 }}>
                          All {autoForm.numberOfGames || 'N'} generated games will attach to this season.
                          {' '}
                          <Link to={`/team/${teamId}/seasons`}
                            className="underline"
                            style={{ color: 'var(--ice)' }}>
                            Manage seasons →
                          </Link>
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-secondary" style={{ lineHeight: 1.5 }}>
                        No seasons yet — games will be created without a season.
                        {' '}
                        <Link to={`/team/${teamId}/seasons`}
                          className="underline"
                          style={{ color: 'var(--ice)' }}>
                          Create one in the Seasons page →
                        </Link>
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Game Day *</label>
                <select className="input-field" required
                  value={autoForm.dayOfWeek} onChange={e => setAutoForm(p => ({ ...p, dayOfWeek: e.target.value }))}>
                  {DAY_OF_WEEK.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Game Time *</label>
                <input type="time" required className="input-field"
                  value={autoForm.gameTime} onChange={e => setAutoForm(p => ({ ...p, gameTime: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Season Start</label>
                <input type="date" className="input-field"
                  value={autoForm.startDate} onChange={e => setAutoForm(p => ({ ...p, startDate: e.target.value }))} />
                <p className="text-xs text-secondary mt-1">Defaults to this week</p>
              </div>
              <div>
                <label className="label">Number of Games *</label>
                <input type="number" min="1" max="50" required className="input-field"
                  value={autoForm.numberOfGames} onChange={e => setAutoForm(p => ({ ...p, numberOfGames: e.target.value }))} />
              </div>
            </div>
            <div>
              <label className="label">Arena Name (optional)</label>
              <input className="input-field" type="text" placeholder="Applies to all games"
                value={autoForm.arenaName} onChange={e => setAutoForm(p => ({ ...p, arenaName: e.target.value }))} />
            </div>
            <div>
              <label className="label">Arena Address (optional)</label>
              <input type="text" className="input-field" autoComplete="street-address"
                value={autoForm.arenaAddress} onChange={e => setAutoForm(p => ({ ...p, arenaAddress: e.target.value }))} />
            </div>
            <div>
              <label className="label">Skip Dates (optional)</label>
              <input className="input-field" type="text" placeholder="2025-12-25, 2026-01-01"
                value={autoForm.skipDates} onChange={e => setAutoForm(p => ({ ...p, skipDates: e.target.value }))} />
              <p className="text-xs text-secondary mt-1">Comma-separated YYYY-MM-DD dates to skip (holidays, etc.)</p>
            </div>

            {/* ── Roster automation ── */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              <p className="label mb-3" style={{ display: 'block', marginBottom: 10 }}>⚙️ Player List Automation (optional)</p>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Auto-Lock Player List</label>
                  <select className="input-field"
                    value={autoForm.rosterLockOffset}
                    onChange={e => setAutoForm(p => ({ ...p, rosterLockOffset: e.target.value }))}>
                    <option value="">Manual only</option>
                    <option value="1">1 hour before</option>
                    <option value="2">2 hours before</option>
                    <option value="3">3 hours before</option>
                    <option value="5">5 hours before</option>
                    <option value="12">12 hours before</option>
                    <option value="24">1 day before</option>
                    <option value="48">2 days before</option>
                  </select>
                  <p className="text-xs text-secondary mt-1">Player list locks automatically at this time</p>
                </div>
                <div>
                  <label className="label">Re-balance Cutoff</label>
                  <select className="input-field"
                    value={autoForm.regenCutoff}
                    onChange={e => setAutoForm(p => ({ ...p, regenCutoff: e.target.value }))}>
                    <option value="">Always allowed</option>
                    <option value="1">Stop 1 hr before</option>
                    <option value="2">Stop 2 hrs before</option>
                    <option value="3">Stop 3 hrs before</option>
                    <option value="5">Stop 5 hrs before</option>
                    <option value="24">Stop 1 day before</option>
                  </select>
                  <p className="text-xs text-secondary mt-1">Block team re-balance after this point</p>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-3 p-3 rounded-lg"
                style={{ background: autoForm.autoBalance ? 'var(--ice-a8)' : 'var(--surface-2)', border: `1px solid ${autoForm.autoBalance ? 'var(--ice-a25)' : 'var(--border)'}`, cursor: 'pointer' }}
                onClick={() => setAutoForm(p => ({ ...p, autoBalance: !p.autoBalance }))}>
                <div style={{
                  width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                  background: autoForm.autoBalance ? 'var(--ice)' : 'var(--surface-3)',
                  border: `1.5px solid ${autoForm.autoBalance ? 'var(--ice)' : 'var(--border)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#000', fontSize: 12, fontWeight: 800,
                }}>
                  {autoForm.autoBalance ? '✓' : ''}
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: autoForm.autoBalance ? 'var(--ice)' : 'var(--text)' }}>
                    ⚖️ Auto-generate balanced teams when player list locks
                  </p>
                  <p className="text-xs text-secondary">AI balances teams automatically — coach still gets notified</p>
                </div>
              </div>
            </div>
            <div className="p-3 rounded-lg text-sm space-y-1"
              style={{ background: 'var(--ice-a7)', border: '1px solid var(--ice-a15)', color: 'var(--ice)' }}>
              <div>Will create <strong>{autoForm.numberOfGames}</strong> games every{' '}
                <strong>{DAY_OF_WEEK[parseInt(autoForm.dayOfWeek)]}</strong> at{' '}
                <strong>{autoForm.gameTime}</strong>
              </div>
              {autoForm.rosterLockOffset && (
                <div style={{ fontSize: 12, opacity: 0.85 }}>🔒 Player list locks <strong>{autoForm.rosterLockOffset}h</strong> before each game</div>
              )}
              {autoForm.autoBalance && (
                <div style={{ fontSize: 12, opacity: 0.85 }}>⚖️ Teams auto-balanced at lock time</div>
              )}
              {autoForm.regenCutoff && (
                <div style={{ fontSize: 12, opacity: 0.85 }}>⏱ Re-balance blocked <strong>{autoForm.regenCutoff}h</strong> before game</div>
              )}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowAutoSchedule(false)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" className="btn-ice flex-1 justify-center">
                <Zap size={15} /> Generate Schedule
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ─── Edit Game Modal ─────────────────────────────────────── */}
      {editGame && (
        <Modal title="Edit Game" onClose={() => setEditGame(null)}>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Date *</label>
                <input type="date" className="input-field" required
                  value={editForm.gameDate} onChange={ef('gameDate')} />
              </div>
              <div>
                <label className="label">Time *</label>
                <input type="time" className="input-field" required
                  value={editForm.gameTime} onChange={ef('gameTime')} />
              </div>
            </div>
            <div>
              <label className="label">Opponent</label>
              <input className="input-field" type="text" placeholder="Kitchener Rangers" maxLength={120}
                value={editForm.opponent} onChange={ef('opponent')} />
            </div>
            <div>
              <label className="label">Arena Name</label>
              <input className="input-field" type="text" placeholder="Leave blank to keep current"
                value={editForm.arenaName} onChange={ef('arenaName')} />
              <p className="text-xs text-secondary mt-1">Changing arena will notify confirmed players by email.</p>
            </div>
            <div>
              <label className="label">Arena Address</label>
              <AddressAutocomplete className="input-field" placeholder="Optional"
                value={editForm.arenaAddress} onChange={(v) => setEditForm(f => ({ ...f, arenaAddress: v }))} />
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input-field" value={editForm.status} onChange={ef('status')}>
                <option value="upcoming">Upcoming</option>
                <option value="roster_open">Players Open</option>
                <option value="locked">Locked</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            {/* Apr 2026 (Tom_Notes B4) — per-game duration override.
                Empty input = inherit team default. */}
            <div>
              <label className="label">
                Duration (minutes) <span className="opacity-50 text-xs">(optional — overrides team default)</span>
              </label>
              <input type="number" className="input-field"
                min={1} max={480}
                placeholder="e.g. 80"
                value={editForm.durationMinutes}
                onChange={ef('durationMinutes')} />
              <p className="text-xs text-secondary mt-1">
                Leave blank to use the team default. Range: 1-480 minutes.
              </p>
            </div>
            {/* Apr 2026 — Game Notes textarea removed per user request. */}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setEditGame(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-ice flex-1 justify-center">
                {saving ? <div className="spinner" /> : 'Save Changes'}
              </button>
            </div>

            {/* May 4 2026 (v2.5.62) — Delete Game action moved inside
                the Edit Game modal per coach request. Was previously a
                standalone red trash button on the schedule card, which
                made accidental deletes too easy. New placement: at the
                bottom of the edit form, visually separated from Save
                by a divider, and styled as the destructive variant.
                Triggers the existing two-step delete confirmation
                modal. ── */}
            <div className="pt-4 mt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={() => {
                  // Capture the game ID, close the edit modal, then
                  // open the delete-confirm modal in the next tick.
                  // We close edit first so the two modals don't stack
                  // on top of each other (which would dim everything
                  // twice and look broken).
                  const id = editGame.id;
                  setEditGame(null);
                  setTimeout(() => setDeleteGameId(id), 0);
                }}
                disabled={saving}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold text-sm transition-all"
                style={{
                  background: 'rgba(255,68,68,0.06)',
                  border: '1px solid rgba(255,68,68,0.18)',
                  color: 'var(--penalty)',
                  opacity: saving ? 0.5 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer',
                }}>
                <Trash2 size={14} /> Delete this game
              </button>
              <p className="text-xs text-center mt-2" style={{ color: 'var(--text-3)' }}>
                You'll get a confirmation prompt before anything is removed.
              </p>
            </div>
          </form>
        </Modal>
      )}

      {/* Apr 2026 — Audience picker for Reminder & Req. Spares.
          Replaces the old "tap and blast" behaviour with a checkbox
          modal that previews exactly who will be emailed. Defaults
          differ by kind:
            • reminder → 'Full-timers who haven't responded' pre-checked
                         (the most common ask — "nudge the people I'm
                         still waiting on")
            • spare    → 'Spares who haven't responded' pre-checked
                         (skips spares already on the waitlist; they
                         already raised their hand)
          Buckets with zero members are disabled so the coach can't
          accidentally pick an empty group. */}
      {audiencePicker && (() => {
        const { kind, gameLabel, buckets, allMembers, statusByUser } = audiencePicker;
        // Apr 2026 — total now includes BOTH bucket selections and
        // per-player picks, deduped via Set. The old computation
        // (sum of bucket sizes) overstated the count when the same
        // user appeared in two buckets, and ignored individual picks
        // entirely; the Set approach matches what sendAudience
        // actually transmits to the backend.
        const previewSet = new Set();
        for (const id of audienceSelection) {
          for (const uid of (buckets[id] || [])) previewSet.add(uid);
        }
        for (const uid of individualSelection) previewSet.add(uid);
        const total = previewSet.size;
        // Filter the all-members list against the search query for
        // the per-player picker. Case-insensitive substring match.
        // Uses 'name' (already-decrypted by the backend) and skips
        // entries without a usable name. Sorted by FT first then
        // alphabetic so the coach scans the list quickly.
        // Apr 2026 — for the 'spare' kind (Invite Spares), FT players
        // are hidden because the backend send-spare-request endpoint
        // filters them out (tm.is_primary = false guard) and would
        // silently drop any FT picks. Showing them here would be a
        // UX trap: the coach ticks Bob the FT, hits Send, gets a
        // "Sent to N players" toast that doesn't mention Bob was
        // skipped. Reminder/jab kinds show everyone — those endpoints
        // accept any team member.
        const q = audienceSearch.trim().toLowerCase();
        const memberList = (allMembers || [])
          .filter(m => m.userId && (m.name || m.placeholderName))
          .filter(m => kind !== 'spare' || !m.isPrimary)
          // Placeholders have no userId via the upstream filter, but
          // double-defending here. Real members always have a userId.
          .map(m => ({
            userId: m.userId,
            name: m.name || m.placeholderName,
            isPrimary: m.isPrimary,
            position: m.position,
            status: statusByUser?.[m.userId] || null,
          }))
          .filter(m => !q || m.name.toLowerCase().includes(q))
          .sort((a, b) => {
            // FT first, then by name
            if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
            return (a.name || '').localeCompare(b.name || '');
          });
        // Definitions for each toggleable group. Order matters — the
        // most relevant defaults appear first.
        const ALL_OPTIONS = [
          { id: 'ft_unresponded',    label: '⏳ Full-timers who haven\'t responded', hint: 'Most common — jab the people you\'re still waiting on' },
          { id: 'ft_in',             label: '✅ Full-timers who said "in"',          hint: 'Confirm details with players already coming' },
          { id: 'ft_out',            label: '❌ Full-timers who said "out"',         hint: 'Reach out if you\'re trying to fill the game' },
          { id: 'spare_unresponded', label: '⏳ Spares who haven\'t responded',     hint: 'Spares who haven\'t said yes or no yet' },
          { id: 'spare_waitlist',    label: '📋 Spares on the waitlist',             hint: 'Already said "in" — only message if you have updates' },
          { id: 'spare_in',          label: '✅ Spares confirmed in',                hint: 'Spares already promoted off the waitlist' },
        ];
        return (
          <Modal
            title={
              kind === 'reminder' ? 'Send Game Reminder'
              : kind === 'jab'    ? 'Jab Players'
              : 'Invite Spares'
            }
            onClose={closeAudiencePicker}>
            <p className="text-xs text-secondary mb-4" style={{ marginTop: -10 }}>
              {gameLabel}
            </p>
            <p className="text-sm mb-4">
              Pick who should get this {
                kind === 'reminder' ? 'reminder'
                : kind === 'jab'    ? 'jab'
                : 'spare invite'
              }. You can select more than one group.
            </p>
            <div className="space-y-2 mb-4">
              {ALL_OPTIONS.map(opt => {
                const count = buckets[opt.id]?.length || 0;
                const checked = audienceSelection.has(opt.id);
                const disabled = count === 0 || audienceSending;
                return (
                  <label
                    key={opt.id}
                    className="flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all"
                    style={{
                      background: checked ? 'rgba(0,212,255,0.10)' : 'var(--surface-2)',
                      border: `1px solid ${checked ? 'rgba(0,212,255,0.35)' : 'var(--border)'}`,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.45 : 1,
                    }}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        const next = new Set(audienceSelection);
                        if (e.target.checked) next.add(opt.id); else next.delete(opt.id);
                        setAudienceSelection(next);
                      }}
                      style={{ marginTop: 3, flexShrink: 0, cursor: disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold">{opt.label}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                          style={{
                            background: count > 0 ? 'var(--ice-a8)' : 'var(--surface-3)',
                            color: count > 0 ? 'var(--ice)' : 'var(--text-2)',
                            fontWeight: 700,
                          }}>
                          {count}
                        </span>
                      </div>
                      <p className="text-xs text-secondary mt-0.5" style={{ lineHeight: 1.35 }}>
                        {opt.hint}
                      </p>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Apr 2026 — Pick specific players by name. Independent of
                the bucket toggles above; ticks here add to the
                recipient set, unticks remove. The bucket toggles are
                the fast path for "everyone in this group"; this list
                is the precise path for "I want to nudge Alice and
                Bob, no one else."
                Search is a case-insensitive substring on the name.
                Status badges show the same RSVP state used in the
                roster cards so the coach has context (e.g. "don't
                jab Bob, he's already 'in'"). The list is scrollable
                with a max-height so a 50-player roster doesn't push
                the Send button off the bottom of the modal. */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-2)' }}>
                  Or pick specific players
                </p>
                {individualSelection.size > 0 && (
                  <button
                    type="button"
                    onClick={() => setIndividualSelection(new Set())}
                    disabled={audienceSending}
                    className="text-xs"
                    style={{ color: 'var(--ice)', fontWeight: 600 }}>
                    Clear ({individualSelection.size})
                  </button>
                )}
              </div>
              <div className="relative mb-2">
                <Search size={14}
                  style={{
                    position: 'absolute', left: 10, top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--text-3)', pointerEvents: 'none',
                  }} />
                <input
                  type="text"
                  value={audienceSearch}
                  onChange={e => setAudienceSearch(e.target.value)}
                  placeholder="Search by name…"
                  disabled={audienceSending}
                  className="input-field"
                  style={{ paddingLeft: 32, fontSize: 13 }}
                />
              </div>
              <div
                style={{
                  maxHeight: 220, overflowY: 'auto',
                  border: '1px solid var(--border)', borderRadius: 8,
                  background: 'var(--surface-2)',
                }}>
                {memberList.length === 0 ? (
                  <div className="text-xs text-center p-4" style={{ color: 'var(--text-3)' }}>
                    {q ? `No players match "${audienceSearch}"` : 'No players to pick from'}
                  </div>
                ) : (
                  memberList.map(m => {
                    const checked = individualSelection.has(m.userId);
                    const statusBadge = m.status === 'in'       ? { label: '✅ in',       color: 'var(--goal)' }
                                      : m.status === 'out'      ? { label: '❌ out',      color: 'var(--penalty)' }
                                      : m.status === 'waitlist' ? { label: '📋 waitlist', color: '#a78bfa' }
                                      : { label: '⏳ no answer', color: 'var(--text-3)' };
                    return (
                      <label
                        key={m.userId}
                        className="flex items-center gap-2.5 px-3 py-2 cursor-pointer"
                        style={{
                          borderTop: '1px solid var(--border)',
                          background: checked ? 'rgba(0,212,255,0.08)' : 'transparent',
                          cursor: audienceSending ? 'not-allowed' : 'pointer',
                        }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={audienceSending}
                          onChange={(e) => {
                            const next = new Set(individualSelection);
                            if (e.target.checked) next.add(m.userId); else next.delete(m.userId);
                            setIndividualSelection(next);
                          }}
                          style={{ flexShrink: 0 }}
                        />
                        <span className="text-sm" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {m.name}
                          {!m.isPrimary && (
                            <span className="text-xs ml-1.5" style={{ color: 'var(--text-3)' }}>
                              spare
                            </span>
                          )}
                        </span>
                        <span className="text-xs" style={{ color: statusBadge.color, flexShrink: 0, fontWeight: 600 }}>
                          {statusBadge.label}
                        </span>
                      </label>
                    );
                  })
                )}
              </div>
            </div>

            <div className="p-3 rounded-lg mb-4 text-xs"
              style={{
                background: total > 0 ? 'rgba(0,255,136,0.06)' : 'var(--surface-2)',
                border: `1px solid ${total > 0 ? 'rgba(0,255,136,0.25)' : 'var(--border)'}`,
                color: total > 0 ? 'var(--goal)' : 'var(--text-2)',
              }}>
              {total === 0
                ? 'Select at least one group above or pick specific players below.'
                : `Will send to ${total} ${total === 1 ? 'player' : 'players'} (duplicates removed).`}
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={closeAudiencePicker}
                disabled={audienceSending}
                className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              <button type="button" onClick={sendAudience}
                disabled={audienceSending || total === 0}
                className="btn-ice flex-1 justify-center">
                {audienceSending
                  ? <div className="spinner" style={{ width: 14, height: 14 }} />
                  : `Send to ${total || 0}`}
              </button>
            </div>
          </Modal>
        );
      })()}

      {/* Apr 2026 — Delete-game confirmation modal. Replaces the
          old "click Delete twice" inline confirm, which was easy to
          misclick on mobile and gave no game context at the moment
          of confirmation. The modal shows the game date + arena
          + RSVP counts so the coach knows EXACTLY which game they're
          about to nuke. The confirm button is the destructive
          (penalty-coloured) one to nudge attention. */}
      {deleteGameId && (() => {
        const game = games.find(g => g.id === deleteGameId);
        if (!game) {
          // Game disappeared between click and render — bail safely.
          // (Could happen if loadGames fired between setDeleteGameId
          // and the next paint.)
          return null;
        }
        const dateLabel = new Date(game.game_date.slice(0, 10) + 'T12:00:00')
          .toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        const inCount = parseInt(game.players_in)       || 0;
        const wlCount = parseInt(game.players_waitlist) || 0;
        return (
          <Modal title="Delete this game?" onClose={() => setDeleteGameId(null)}>
            <div className="mb-4 p-3 rounded-lg" style={{
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
            }}>
              <p className="text-sm font-semibold mb-1">{dateLabel}</p>
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                {game.game_time || ''}{game.game_time && game.arena_name ? ' · ' : ''}{game.arena_name || ''}
              </p>
              {(inCount > 0 || wlCount > 0) && (
                <p className="text-xs mt-2" style={{ color: 'var(--text-2)' }}>
                  {inCount > 0 && <>✅ {inCount} {inCount === 1 ? 'player' : 'players'} confirmed in</>}
                  {inCount > 0 && wlCount > 0 && <>{' · '}</>}
                  {wlCount > 0 && <>📋 {wlCount} on waitlist</>}
                </p>
              )}
            </div>
            <p className="text-sm mb-2">
              This permanently removes the game and all of its RSVPs, balanced
              teams, drinks, and chat. This cannot be undone.
            </p>
            <p className="text-xs mb-5" style={{ color: 'var(--text-3)' }}>
              Players who already RSVPed will not be notified automatically — let
              them know separately if needed.
            </p>
            <div className="flex gap-3">
              <button type="button"
                onClick={() => setDeleteGameId(null)}
                className="btn-ghost flex-1 justify-center">
                Cancel
              </button>
              <button type="button"
                onClick={() => deleteGame(game.id)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all flex-1"
                style={{
                  background: 'var(--penalty)',
                  color: '#fff',
                  border: '1px solid var(--penalty)',
                }}>
                <Trash2 size={14} />
                Delete game
              </button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// May 2026 — z-index bumped 50 → 500 to stop dialog action buttons
// (Schedule Game / Save / Delete) being hidden behind the mobile
// BottomNav (z-400). Sticky header with an X close button gives users
// a clear top-right dismiss target on long forms (e.g. Schedule Game).
//
// v2.5.46 — additionally a mobile-only bottom spacer ensures the
// modal's bottom action area sits clear of the bottom nav region even
// for tall content (90vh max-height could overlap the nav otherwise).
function Modal({ title, onClose, children }) {
  // 2026-09-16 — keyboard-safe sheet (coach report: "the message box
  // disappears when I tap it on mobile"). The sheet shrinks with the
  // keyboard (--mobile-h) but its scroll position didn't follow the
  // focused field, so a textarea at the bottom of a long list ended up
  // clipped below the sheet's new bottom edge. The hook re-scrolls the
  // focused input into view once the keyboard has settled and pins the
  // wrapper to the visible top on iOS Safari. See hooks/useKeyboardSafeSheet.js.
  const { sheetRef, vvTop } = useKeyboardSafeSheet();
  // May 4 2026 (v2.5.62) — body scroll lock + mobile-sheet pattern.
  // Coach feedback: modals on mobile felt "non-native" because the
  // background page would drift horizontally during scroll, and the
  // modal was centered as a card with margins around it instead of
  // sliding up as a sheet. Switched to the same pattern GamePage and
  // DashboardPage modals already use:
  //   • Mobile (<sm): items-end + p-0 → modal fills width, sticks to
  //     bottom, slides up as a sheet. Native iOS/Android feel.
  //   • Desktop (sm+): items-center + p-4 → centered card, unchanged.
  //   • Body scroll lock while open so the underlying page can't
  //     horizontally drift behind the modal during touch scrolling.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    const prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.touchAction = prevTouchAction;
    };
  }, []);
  return (
    <div className="fixed left-0 right-0 top-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{
        // May 8 2026 (v2.5.86) — was `inset-0`. See GamePage Modal
        // for the full rationale: the bottom-anchor in `inset-0`
        // extended the wrapper behind the iOS keyboard, putting the
        // sheet's lower half (where the textarea cursor and Send
        // button live) under the keyboard. Switching to a
        // top-anchored wrapper with explicit --mobile-h height
        // (visualViewport.height, set live by MobileLayout) makes
        // items-end align to the visible bottom — just above the
        // keyboard — instead of the layout-viewport bottom.
        height: 'var(--mobile-h, 100dvh)',
        // 2026-09-16 — see GamePage Modal: pin to the visible top while
        // an input in this sheet is focused (iOS Safari visual-viewport
        // scroll). 0 elsewhere.
        top: vvTop,
        zIndex: 500,
        // May 4 2026 (v2.5.63) — touch-action: none on the overlay
        // ignores ALL gestures over the dim layer except taps
        // (handled below). This prevents iOS Safari's swipe-back
        // and pull-to-refresh from triggering through the dim area.
        touchAction: 'none',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={sheetRef} className="w-full sm:max-w-md animate-slide-up modal-sheet"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          // May 8 2026 (v2.5.86) — was `85dvh`. dvh doesn't shrink
          // when iOS Safari opens the keyboard, so the modal could
          // overflow into the keyboard area. --mobile-h is updated
          // live to visualViewport.height by MobileLayout's observer
          // and shrinks correctly. Multiplying with 0.85 preserves
          // the prior visual proportion (15% breathing room above).
          maxHeight: 'calc(var(--mobile-h, 100dvh) * 0.85)',
          overflowY: 'auto',
          // touch-action: pan-y + overscroll-behavior: contain are
          // applied via the .modal-sheet class with !important so
          // they override the global * { touch-action: manipulation }
          // rule at index.css:364. Keeping them in CSS rather than
          // inline keeps the override predictable.
          WebkitOverflowScrolling: 'touch',
          padding: '0 16px calc(env(safe-area-inset-bottom, 0px) + 20px)',
        }}>
        <div style={{
          position: 'sticky', top: 0, zIndex: 1,
          background: 'var(--surface)',
          margin: '0 -16px', padding: '16px 16px 12px',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <h2 className="section-title text-xl" style={{ flex: 1, margin: 0 }}>{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              flex: '0 0 auto',
              width: 36, height: 36,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              cursor: 'pointer', padding: 0,
              WebkitTapHighlightColor: 'transparent',
            }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ paddingTop: 4 }}>{children}</div>
        {/* v2.5.46 — mobile-only nav-clearance spacer. */}
        <div className="sm:hidden" aria-hidden style={{ height: 70 }} />
      </div>
    </div>
  );
}
